// Rotas falsas de Pessoas (GIY-067; Parte 6.5): pessoas de rede com visibilidade por tenant (fora da projeção → 404, REQ-PPL-015),
// vínculos com as TRAVAS do servidor (sobreposição 422, Matriz `NotAllowed` 423, CLT → flexível 423 salvo exceção aprovada em
// duas chaves, centro só com a própria pessoa jurídica), escala só em vínculo fixo (flexível → 422), disponibilidade,
// ausências com prévia de impacto, licenças e certificações (verificação por outra pessoa), capacidades, onboarding e
// offboarding, importação CSV (atômica/parcial, relatório só com coluna e código), point-in-time e "Meu cadastro".
// Regras de escopo iguais às do servidor: `/network/*` sem acesso de rede → 403 PLT_NETWORK_SCOPE_REQUIRED; `/tenants/{t}`
// fora dos contextos e sem escopo de rede → 404; permissão pelo papel (`rolePermissions`, Anexo A); propósito nas leituras
// sensíveis (422 AUTHZ_PURPOSE_REQUIRED); step-up na Matriz, na exceção de transição e na importação (401); `If-Match` nos
// `PUT` (412); razão de alteração em dado regulado (422 PLT_CHANGE_REASON_REQUIRED). A segunda chave das duas aprovações
// passa pela rota genérica `POST /authz/two-key/{id}/second-key` (interceptada aqui só para as aprovações de pessoas).
import type { MeResponse } from '@giyus/api-client';

import type { FakeSessionAuth } from '../auth/FakeAuthProvider';
import { isSuperAdminUser } from '../auth/greenfield';
import {
  ALL_FULL_ADMIN_PERMISSIONS,
  networkRolePermissions,
  tenantRolePermissions,
} from '../permissions/rolePermissions';
import { type FakeAuthzState, appendAudit } from './authzStore';
import { FakeApiError, jsonResponse, problemResponse, readJsonBody } from './http';
import {
  BOARDS,
  CERTIFICATION_TYPES,
  FLEXIBLE_TYPES,
  FUNCTIONS,
  FUNCTION_CODES,
  type FakeAbsence,
  type FakeApproval,
  type FakeAvailability,
  type FakeCertification,
  type FakeException,
  type FakeImportBatch,
  type FakeLicense,
  type FakeLink,
  type FakeMatrixVersion,
  type FakePeopleState,
  type FakePeopleTenant,
  type FakePerson,
  type FakeSchedule,
  type ImportRowResponse,
  type PersonContactResponse,
  type PersonSkillResponse,
  HOLDING_LEGAL_NAME,
  LINK_TYPE_LIST,
  SKILLS,
  buildOffboardingItems,
  buildOnboardingItems,
  credentialStatus,
  daysBetween,
  displayNameOf,
  etagOf,
  fakePeopleId,
  formatFederalCode,
  indicationOf,
  isValidCnpj,
  isValidCpf,
  linkStatusOn,
  maskFederalCode,
  publishedMatrix,
  shiftHours,
  toApprovalResponse,
  toLinkResponse,
  toMatrixResponse,
  toMatrixSummary,
  toPersonDetail,
  toPersonSummary,
  todayIso,
} from './peopleStore';
import { HOLDING_LEGAL_ENTITY_ID, formatCnpj } from './tenancyStore';

export interface PeopleRouteContext {
  readonly state: FakePeopleState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
  readonly auth: FakeSessionAuth;
  /** Estado de autorização: recebe os eventos de auditoria (a ficha mostra o histórico pela trilha). */
  readonly authz?: FakeAuthzState | undefined;
}

type Scope =
  { readonly kind: 'network' } | { readonly kind: 'tenant'; readonly tenant: FakePeopleTenant };
type Params = Readonly<Record<string, string>>;
type Handler = (params: Params) => Promise<Response> | Response;

interface RouteDef {
  readonly method: string;
  readonly pattern: readonly string[];
  readonly handler: Handler;
}

interface FieldErrorItem {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

const PURPOSES = new Set([
  'VisitConfirmation',
  'ClinicalCare',
  'StudySetup',
  'QualityAudit',
  'PrivacyRequest',
  'Export',
  'Support',
  'LegalRequest',
  'Other',
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEEK_DAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const LOCALES = new Set(['pt-BR', 'en-US']);
const ABSENCE_KINDS = new Set(['Vacation', 'Leave', 'Withdrawal', 'Training', 'Travel', 'Other']);
const CONTACT_KINDS = new Set(['Email', 'Phone', 'WhatsApp']);
const MAX_PAGE_SIZE = 200;
const DEFAULT_PAGE_SIZE = 20;
const TWO_KEY_HOURS = 72;
const IMPORT_MAX_BYTES = 2 * 1024 * 1024;
const IMPORT_KNOWN_COLUMNS = [
  'givenName',
  'familyName',
  'socialName',
  'country',
  'federalCode',
  'birthDate',
  'preferredLocale',
  'email',
  'phone',
  'linkType',
  'functionCode',
  'effectiveFrom',
  'effectiveTo',
  'pjCnpj',
  'cooperativeMemberNumber',
  'primarySiteCode',
  'classificationJustification',
];
const IMPORT_REQUIRED_COLUMNS = [
  'givenName',
  'familyName',
  'federalCode',
  'birthDate',
  'linkType',
  'functionCode',
  'effectiveFrom',
];

// ---- Utilidades de corpo e validação ----

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Texto aparado e não vazio; `null` quando ausente, nulo ou vazio. */
function str(body: unknown, field: string): string | null {
  const value = record(body)[field];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function has(body: unknown, field: string): boolean {
  const value = record(body)[field];
  return value !== undefined && value !== null;
}

function fieldError(field: string, code: string): FieldErrorItem {
  return { field, code, message: code };
}

function validation(errors: readonly FieldErrorItem[]): FakeApiError {
  return new FakeApiError(422, 'PLT_VALIDATION', { errors });
}

function failIf(errors: readonly FieldErrorItem[]): void {
  if (errors.length > 0) {
    throw validation(errors);
  }
}

function isIsoDate(value: string | null): value is string {
  if (value === null || !ISO_DATE.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** Campo de data obrigatório: `required` (ausente) ou `invalid` (formato/calendário). */
function checkDate(errors: FieldErrorItem[], field: string, value: string | null): void {
  if (value === null) {
    errors.push(fieldError(field, 'required'));
  } else if (!isIsoDate(value)) {
    errors.push(fieldError(field, 'invalid'));
  }
}

function timeToMinutes(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function requireStepUp(ctx: PeopleRouteContext): void {
  if (!ctx.auth.isRecentlyAuthenticated()) {
    throw new FakeApiError(401, 'PLT_STEP_UP_REQUIRED', { error: 'step_up_required' });
  }
}

function requirePurpose(request: Request): string {
  const header = request.headers.get('X-Purpose');
  if (!header) {
    throw new FakeApiError(422, 'AUTHZ_PURPOSE_REQUIRED');
  }
  const [code, ...rest] = header.split(';');
  const purposeCode = (code ?? '').trim();
  const hasText = rest.join(';').includes('text=');
  if (!PURPOSES.has(purposeCode) || (purposeCode === 'Other' && !hasText)) {
    throw new FakeApiError(422, 'AUTHZ_PURPOSE_INVALID');
  }
  return purposeCode;
}

function requireIfMatch(request: Request, current: string): void {
  const header = request.headers.get('If-Match');
  if (!header) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_REQUIRED');
  }
  if (header !== '*' && header !== current) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_FAILED');
  }
}

interface Reason {
  readonly code: string;
  readonly text: string | null;
}

function requireReason(body: unknown): Reason {
  const reason = record(record(body)['changeReason']);
  const code = typeof reason['code'] === 'string' ? reason['code'].trim() : '';
  if (code === '') {
    throw new FakeApiError(422, 'PLT_CHANGE_REASON_REQUIRED');
  }
  const reasonText = reason['text'];
  return {
    code,
    text: typeof reasonText === 'string' && reasonText.trim() !== '' ? reasonText.trim() : null,
  };
}

function ok(body: unknown, etag?: string): Response {
  return jsonResponse(body, 200, etag ? { ETag: etag } : undefined);
}

function created(body: unknown, etag?: string): Response {
  return jsonResponse(body, 201, etag ? { ETag: etag } : undefined);
}

function match(rest: readonly string[], pattern: readonly string[]): Params | null {
  if (rest.length !== pattern.length) {
    return null;
  }
  const params: Record<string, string> = {};
  for (let index = 0; index < pattern.length; index += 1) {
    const expected = pattern[index] ?? '';
    const actual = rest[index] ?? '';
    if (expected.startsWith(':')) {
      params[expected.slice(1)] = actual;
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
}

async function dispatch(
  request: Request,
  rest: readonly string[],
  routes: readonly RouteDef[],
): Promise<Response | null> {
  for (const route of routes) {
    if (route.method !== request.method) {
      continue;
    }
    const params = match(rest, route.pattern);
    if (params) {
      return await route.handler(params);
    }
  }
  return null;
}

function param(params: Params, name: string): string {
  return params[name] ?? '';
}

// ---- Escopo e permissões ----

function networkPermissions(ctx: PeopleRouteContext): Set<string> {
  if (isSuperAdminUser(ctx.me)) {
    return new Set(ALL_FULL_ADMIN_PERMISSIONS);
  }
  const result = new Set<string>();
  if (ctx.me.networkAccess.hasAccess) {
    for (const role of ctx.me.networkAccess.roles) {
      for (const permission of networkRolePermissions(role)) {
        result.add(permission);
      }
    }
  }
  return result;
}

function permissionsOf(ctx: PeopleRouteContext, scope: Scope): Set<string> {
  const result = networkPermissions(ctx);
  if (scope.kind === 'network') {
    return result;
  }
  const context = ctx.me.contexts.find((item) => item.tenantId === scope.tenant.id);
  for (const effective of context?.effectiveRoles ?? []) {
    for (const permission of tenantRolePermissions(effective.role)) {
      result.add(permission);
    }
  }
  // Quem tem leitura de rede lê o tenant (escopo de rede cobre todos os centros).
  if (result.has('person.read.network')) {
    result.add('person.read');
  }
  return result;
}

function need(ctx: PeopleRouteContext, scope: Scope, permission: string): void {
  if (!permissionsOf(ctx, scope).has(permission)) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
}

function readPermission(scope: Scope): string {
  return scope.kind === 'network' ? 'person.read.network' : 'person.read';
}

function managePermission(scope: Scope): string {
  return scope.kind === 'network' ? 'person.manage.network' : 'person.manage';
}

function tenantIdOf(scope: Scope): string | null {
  return scope.kind === 'tenant' ? scope.tenant.id : null;
}

// ---- Auditoria ----

interface AuditInput {
  readonly eventType: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly tenantId?: string | null;
  readonly after?: unknown;
  readonly reason?: Reason | null;
  readonly purpose?: string | null;
  readonly permission?: string | null;
}

/**
 * Pessoa a que uma entidade auditada diz respeito (OPEN-094): a própria ficha, ou a `personId` da linha — o mesmo que o `PeopleAuditEventNamer` do servidor declara para
 * correlacionar o evento ao histórico por pessoa.
 */
function personIdOfEntity(
  ctx: PeopleRouteContext,
  entityType: string,
  entityId: string,
): string | null {
  if (entityType === 'Person') {
    return entityId;
  }
  const state = ctx.state;
  const rows: readonly { readonly id: string; readonly personId: string }[][] = [
    state.links,
    state.licenses,
    state.certifications,
    state.skills,
    state.schedules,
    state.availabilities,
    state.absences,
    state.onboarding,
    state.offboarding,
    state.exceptions,
  ];
  for (const list of rows) {
    const found = list.find((row) => row.id === entityId);
    if (found) {
      return found.personId;
    }
  }
  return null;
}

function audit(ctx: PeopleRouteContext, event: AuditInput): void {
  if (!ctx.authz) {
    return;
  }
  appendAudit(ctx.authz, {
    personId: personIdOfEntity(ctx, event.entityType, event.entityId),
    actorUserId: ctx.me.user.id,
    actorDisplayName: ctx.me.user.displayName,
    actorKind: 'user',
    actorRoleSnapshot: JSON.stringify([...ctx.me.networkAccess.roles].sort()),
    actorLinkSnapshot: null,
    tenantId: event.tenantId ?? null,
    studySiteId: null,
    module: 'PPL',
    eventType: event.eventType,
    entityType: event.entityType,
    entityId: event.entityId,
    before: null,
    after: event.after === undefined ? null : JSON.stringify(event.after),
    changeReasonCode: event.reason?.code ?? null,
    changeReasonText: event.reason?.text ?? null,
    declaredPurpose: event.purpose ?? null,
    permission: event.permission ?? null,
    correlationId: ctx.correlationId,
  });
}

// ---- Pessoas e visibilidade ----

function findPerson(ctx: PeopleRouteContext, personId: string): FakePerson | undefined {
  return ctx.state.people.find((person) => person.id === personId);
}

/** A pessoa existe e o escopo a vê (rede: toda; tenant: só com razão de visibilidade ativa) — senão 404. */
function visiblePerson(ctx: PeopleRouteContext, scope: Scope, personId: string): FakePerson {
  const person = findPerson(ctx, personId);
  if (!person) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  if (scope.kind === 'tenant' && (person.visibility[scope.tenant.id] ?? []).length === 0) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return person;
}

/** Vínculos que o escopo enxerga: rede todos; tenant os da própria PJ e o da holding só com atribuição/delegação. */
function visibleLinks(ctx: PeopleRouteContext, scope: Scope, person: FakePerson): FakeLink[] {
  const all = ctx.state.links.filter((link) => link.personId === person.id);
  if (scope.kind === 'network') {
    return all;
  }
  const reasons = person.visibility[scope.tenant.id] ?? [];
  const holdingVisible = reasons.includes('Assignment') || reasons.includes('Delegation');
  return all.filter(
    (link) => link.tenantId === scope.tenant.id || (link.tenantId === null && holdingVisible),
  );
}

function visibleLink(
  ctx: PeopleRouteContext,
  scope: Scope,
  person: FakePerson,
  linkId: string,
): FakeLink {
  const found = visibleLinks(ctx, scope, person).find((link) => link.id === linkId);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function visibilityOf(scope: Scope, person: FakePerson): string[] {
  return scope.kind === 'tenant' ? [...(person.visibility[scope.tenant.id] ?? [])] : [];
}

function bump(person: FakePerson): void {
  person.version += 1;
  person.modifiedAt = new Date().toISOString();
}

function addVisibility(
  person: FakePerson,
  tenantId: string | null,
  reason: 'Link' | 'Assignment' | 'Delegation',
): void {
  if (tenantId === null) {
    return;
  }
  const reasons = person.visibility[tenantId] ?? [];
  if (!reasons.includes(reason)) {
    person.visibility[tenantId] = [...reasons, reason];
  }
}

// ---- Normalização de pessoa, contatos e vínculo ----

function normalizeContacts(
  ctx: PeopleRouteContext,
  value: unknown,
  field: string,
): PersonContactResponse[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (!Array.isArray(value)) {
    throw validation([fieldError(field, 'invalid')]);
  }
  const errors: FieldErrorItem[] = [];
  const result: PersonContactResponse[] = [];
  const seen = new Set<string>();
  const primaries = new Set<string>();
  value.forEach((raw: unknown, index) => {
    const prefix = `${field}[${String(index)}]`;
    const kind = typeof record(raw)['kind'] === 'string' ? (record(raw)['kind'] as string) : '';
    if (!CONTACT_KINDS.has(kind)) {
      errors.push(fieldError(`${prefix}.kind`, 'invalid'));
      return;
    }
    let contactValue = str(raw, 'value');
    if (contactValue === null) {
      errors.push(fieldError(`${prefix}.value`, 'required'));
      return;
    }
    if (contactValue.length > 200) {
      errors.push(fieldError(`${prefix}.value`, 'too_long'));
      return;
    }
    if (kind === 'Email') {
      if (!EMAIL.test(contactValue)) {
        errors.push(fieldError(`${prefix}.value`, 'invalid_email'));
        return;
      }
      contactValue = contactValue.toLowerCase();
    } else {
      const digits = contactValue.replace(/\D/g, '');
      if (digits.length < 8 || digits.length > 20) {
        errors.push(fieldError(`${prefix}.value`, 'invalid_phone'));
        return;
      }
    }
    if (seen.has(`${kind}:${contactValue}`)) {
      errors.push(fieldError(`${prefix}.value`, 'duplicate'));
      return;
    }
    seen.add(`${kind}:${contactValue}`);
    const isPrimary = record(raw)['isPrimary'] === true;
    if (isPrimary && primaries.has(kind)) {
      errors.push(fieldError(`${prefix}.isPrimary`, 'multiple_primary'));
      return;
    }
    if (isPrimary) {
      primaries.add(kind);
    }
    result.push({
      id: fakePeopleId(ctx.state),
      kind,
      value: contactValue,
      isPrimary,
      verifiedAt: null,
    });
  });
  failIf(errors);
  return result;
}

interface NormalizedPerson {
  readonly givenName: string;
  readonly familyName: string;
  readonly socialName: string | null;
  readonly country: string;
  readonly federalCode: string | null;
  readonly birthDate: string | null;
  readonly preferredLocale: string;
  readonly contacts: PersonContactResponse[];
}

/** Regras de `PersonRules.Normalize`: CPF só para `BR` (422 PPL_INVALID_CPF), formato livre ≤ 32 nos demais países. */
function normalizePerson(
  ctx: PeopleRouteContext,
  body: unknown,
  requireIdentifiers: boolean,
  prefix = '',
): NormalizedPerson {
  const field = (name: string): string => `${prefix}${name}`;
  const errors: FieldErrorItem[] = [];
  const givenName = str(body, 'givenName');
  const familyName = str(body, 'familyName');
  if (givenName === null) {
    errors.push(fieldError(field('givenName'), 'required'));
  } else if (givenName.length > 100) {
    errors.push(fieldError(field('givenName'), 'too_long'));
  }
  if (familyName === null) {
    errors.push(fieldError(field('familyName'), 'required'));
  } else if (familyName.length > 100) {
    errors.push(fieldError(field('familyName'), 'too_long'));
  }
  const socialName = str(body, 'socialName');
  if (socialName !== null && socialName.length > 100) {
    errors.push(fieldError(field('socialName'), 'too_long'));
  }
  const country = (str(body, 'country') ?? 'BR').toUpperCase();
  if (!/^[A-Z]{2}$/.test(country)) {
    errors.push(fieldError(field('country'), 'invalid'));
  }
  let federalCode: string | null = null;
  const rawFederal = str(body, 'federalCode');
  if (rawFederal !== null) {
    if (country === 'BR') {
      if (!isValidCpf(rawFederal)) {
        throw new FakeApiError(422, 'PPL_INVALID_CPF', {
          errors: [fieldError(field('federalCode'), 'invalid')],
        });
      }
      federalCode = rawFederal.replace(/\D/g, '');
    } else if (rawFederal.length > 32) {
      errors.push(fieldError(field('federalCode'), 'too_long'));
    } else {
      federalCode = rawFederal;
    }
  } else if (requireIdentifiers) {
    errors.push(fieldError(field('federalCode'), 'required'));
  }
  let birthDate: string | null = null;
  const rawBirth = str(body, 'birthDate');
  if (rawBirth !== null) {
    if (!isIsoDate(rawBirth) || rawBirth > todayIso() || rawBirth < '1900-01-01') {
      errors.push(fieldError(field('birthDate'), 'invalid'));
    } else {
      birthDate = rawBirth;
    }
  } else if (requireIdentifiers) {
    errors.push(fieldError(field('birthDate'), 'required'));
  }
  const rawLocale = str(body, 'preferredLocale');
  const preferredLocale = rawLocale ?? 'pt-BR';
  if (!LOCALES.has(preferredLocale)) {
    errors.push(fieldError(field('preferredLocale'), 'invalid'));
  }
  let contacts: PersonContactResponse[] = [];
  try {
    contacts = normalizeContacts(ctx, record(body)['contacts'], field('contacts'));
  } catch (error: unknown) {
    if (error instanceof FakeApiError && error.status === 422) {
      errors.push(...((error.extra?.['errors'] as FieldErrorItem[] | undefined) ?? []));
    } else {
      throw error;
    }
  }
  failIf(errors);
  return {
    givenName: givenName ?? '',
    familyName: familyName ?? '',
    socialName,
    country,
    federalCode,
    birthDate,
    preferredLocale,
    contacts,
  };
}

function findDuplicate(
  ctx: PeopleRouteContext,
  country: string,
  federalCode: string,
  ignore?: string,
): boolean {
  return ctx.state.people.some(
    (person) =>
      person.id !== ignore &&
      person.country === country &&
      person.federalCode !== null &&
      person.federalCode.toUpperCase() === federalCode.toUpperCase(),
  );
}

function legalEntityKnown(
  ctx: PeopleRouteContext,
  legalEntityId: string,
): { tenant: FakePeopleTenant | null } | null {
  if (legalEntityId === HOLDING_LEGAL_ENTITY_ID) {
    return { tenant: null };
  }
  const tenant = ctx.state.tenants.find((item) => item.legalEntityId === legalEntityId);
  return tenant ? { tenant } : null;
}

function rangesOverlap(
  aFrom: string,
  aTo: string | null,
  bFrom: string,
  bTo: string | null,
): boolean {
  return aFrom <= (bTo ?? '9999-12-31') && bFrom <= (aTo ?? '9999-12-31');
}

interface LinkDraft {
  readonly link: FakeLink;
  /** Exceção de transição aprovada que este vínculo consome (um único vínculo — o flexível seguinte volta a ser bloqueado). */
  readonly consumes: FakeException | null;
}

/** Cria (sem gravar) o vínculo com todas as travas: campos, centro × holding, sobreposição, Matriz, CLT → flexível. */
function buildLink(
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  existing: readonly FakeLink[],
  body: unknown,
  prefix = '',
): LinkDraft {
  const field = (name: string): string => `${prefix}${name}`;
  const errors: FieldErrorItem[] = [];
  // No centro a pessoa jurídica é a do próprio tenant e pode vir omitida (o servidor a resolve); na rede é obrigatória.
  const legalEntityId =
    str(body, 'legalEntityId') ?? (scope.kind === 'tenant' ? scope.tenant.legalEntityId : null);
  if (legalEntityId === null || !UUID.test(legalEntityId)) {
    errors.push(
      fieldError(field('legalEntityId'), legalEntityId === null ? 'required' : 'invalid'),
    );
  }
  if (
    scope.kind === 'tenant' &&
    legalEntityId !== null &&
    legalEntityId !== scope.tenant.legalEntityId
  ) {
    throw new FakeApiError(422, 'PPL_LINK_OUT_OF_TENANT');
  }
  const linkType = str(body, 'linkType');
  if (linkType === null || !LINK_TYPE_LIST.some((item) => item === linkType)) {
    errors.push(fieldError(field('linkType'), linkType === null ? 'required' : 'invalid'));
  }
  const functionCode = str(body, 'functionCode');
  if (functionCode === null || !FUNCTION_CODES.has(functionCode)) {
    errors.push(fieldError(field('functionCode'), functionCode === null ? 'required' : 'unknown'));
  }
  const effectiveFrom = str(body, 'effectiveFrom');
  checkDate(errors, field('effectiveFrom'), effectiveFrom);
  const effectiveTo = str(body, 'effectiveTo');
  if (
    effectiveTo !== null &&
    (!isIsoDate(effectiveTo) || (effectiveFrom !== null && effectiveTo < effectiveFrom))
  ) {
    errors.push(fieldError(field('effectiveTo'), 'invalid'));
  }
  const rawCnpj = str(body, 'pjCnpj');
  if (linkType === 'PJ') {
    if (rawCnpj === null) {
      errors.push(fieldError(field('pjCnpj'), 'required'));
    } else if (!isValidCnpj(rawCnpj)) {
      errors.push(fieldError(field('pjCnpj'), 'invalid'));
    }
  }
  const member = str(body, 'cooperativeMemberNumber');
  if (linkType === 'CooperadoCPESQ') {
    if (member === null) {
      errors.push(fieldError(field('cooperativeMemberNumber'), 'required'));
    } else if (member.length > 64) {
      errors.push(fieldError(field('cooperativeMemberNumber'), 'too_long'));
    }
  }
  const justification = str(body, 'classificationJustification');
  if (justification !== null && justification.length > 2000) {
    errors.push(fieldError(field('classificationJustification'), 'too_long'));
  }
  const entity =
    legalEntityId !== null && UUID.test(legalEntityId)
      ? legalEntityKnown(ctx, legalEntityId)
      : null;
  if (legalEntityId !== null && UUID.test(legalEntityId) && entity === null) {
    errors.push(fieldError(field('legalEntityId'), 'unknown'));
  }
  const primarySiteId = str(body, 'primarySiteId');
  const additionalRaw = record(body)['additionalSiteIds'];
  const additionalSiteIds = Array.isArray(additionalRaw)
    ? additionalRaw.filter((item): item is string => typeof item === 'string')
    : [];
  errors.push(
    ...siteErrors(
      entity?.tenant ?? null,
      entity !== null,
      primarySiteId,
      additionalSiteIds,
      prefix,
    ),
  );
  failIf(errors);

  const type = linkType ?? '';
  const code = functionCode ?? '';
  const from = effectiveFrom ?? '';
  const to = effectiveTo;
  const entityId = legalEntityId ?? '';
  const today = todayIso();
  if (
    existing.some(
      (item) =>
        item.legalEntityId === entityId &&
        item.functionCode === code &&
        item.linkType === type &&
        rangesOverlap(item.effectiveFrom, item.effectiveTo, from, to),
    )
  ) {
    throw new FakeApiError(422, 'PPL_LINK_OVERLAP');
  }
  const indication = indicationOf(ctx.state, code, type);
  if (indication === 'NotAllowed') {
    auditBlocked(ctx, scope, personId, entityId, code, type, 'PPL_LINK_NOT_ALLOWED');
    throw new FakeApiError(423, 'PPL_LINK_NOT_ALLOWED');
  }
  let consumes: FakeException | null = null;
  if (FLEXIBLE_TYPES.has(type)) {
    const clt = existing.find(
      (item) =>
        item.linkType === 'CLT' &&
        item.legalEntityId === entityId &&
        item.functionCode === code &&
        ['Ended', 'Expired'].includes(linkStatusOn(item, today)),
    );
    if (clt) {
      consumes =
        ctx.state.exceptions.find(
          (item) => item.fromLinkId === clt.id && item.status === 'Approved',
        ) ?? null;
      if (!consumes) {
        auditBlocked(ctx, scope, personId, entityId, code, type, 'PPL_BLOCKED_TRANSITION');
        throw new FakeApiError(423, 'PPL_BLOCKED_TRANSITION');
      }
    }
  }
  const opinion = str(body, 'legalOpinionDocumentId');
  if (opinion !== null && !UUID.test(opinion)) {
    throw validation([fieldError(field('legalOpinionDocumentId'), 'invalid')]);
  }
  if (indication === 'CaseByCase' && justification === null) {
    throw validation([fieldError(field('classificationJustification'), 'required')]);
  }
  const link: FakeLink = {
    id: fakePeopleId(ctx.state),
    personId,
    legalEntityId: entityId,
    tenantId: entity?.tenant?.id ?? null,
    linkType: type,
    functionCode: code,
    effectiveFrom: from,
    effectiveTo: to,
    status: linkStatusOn({ effectiveFrom: from, effectiveTo: to, endedAt: null }, today),
    classificationStatus:
      indication === 'CaseByCase' && opinion === null ? 'PendingOpinion' : 'Valid',
    classificationJustification: justification,
    legalOpinionDocumentId: opinion,
    contractDocumentId: str(body, 'contractDocumentId'),
    pjCnpj: type === 'PJ' && rawCnpj !== null ? formatCnpj(rawCnpj.replace(/\D/g, '')) : null,
    cooperativeMemberNumber: type === 'CooperadoCPESQ' ? member : null,
    primarySiteId,
    additionalSiteIds,
    endReason: null,
    endedAt: null,
    endedBy: null,
    createdAt: new Date().toISOString(),
    modifiedAt: null,
    version: 1,
  };
  return { link, consumes };
}

/** Site principal e de atuação: centro exige site ativo do tenant; holding nenhum. */
function siteErrors(
  tenant: FakePeopleTenant | null,
  entityKnown: boolean,
  primarySiteId: string | null,
  additionalSiteIds: readonly string[],
  prefix: string,
): FieldErrorItem[] {
  const errors: FieldErrorItem[] = [];
  if (!entityKnown) {
    return errors;
  }
  if (tenant === null) {
    if (primarySiteId !== null) {
      errors.push(fieldError(`${prefix}primarySiteId`, 'not_allowed_for_holding'));
    }
    if (additionalSiteIds.length > 0) {
      errors.push(fieldError(`${prefix}additionalSiteIds`, 'not_allowed_for_holding'));
    }
    return errors;
  }
  if (primarySiteId === null) {
    errors.push(fieldError(`${prefix}primarySiteId`, 'required'));
  } else if (!tenant.siteIds.includes(primarySiteId)) {
    errors.push(fieldError(`${prefix}primarySiteId`, 'not_an_active_site'));
  }
  if (additionalSiteIds.some((id) => !tenant.siteIds.includes(id))) {
    errors.push(fieldError(`${prefix}additionalSiteIds`, 'not_an_active_site'));
  }
  return errors;
}

/** Tentativa bloqueada: auditada (`PPL.LINK_BLOCKED`) mesmo sem nada gravado (423, nunca aviso). */
function auditBlocked(
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  legalEntityId: string,
  functionCode: string,
  linkType: string,
  reason: string,
): void {
  audit(ctx, {
    eventType: 'PPL.LINK_BLOCKED',
    entityType: 'Person',
    entityId: personId,
    tenantId: tenantIdOf(scope),
    after: { legalEntityId, functionCode, linkType, reason },
  });
}

function commitLink(
  ctx: PeopleRouteContext,
  scope: Scope,
  person: FakePerson,
  draft: LinkDraft,
): FakeLink {
  ctx.state.links.push(draft.link);
  addVisibility(person, draft.link.tenantId, 'Link');
  if (draft.consumes) {
    draft.consumes.status = 'Consumed';
    draft.consumes.consumedAt = new Date().toISOString();
    draft.consumes.consumedByLinkId = draft.link.id;
    draft.consumes.version += 1;
    audit(ctx, {
      eventType: 'PPL.LINK_TRANSITION_EXCEPTION_CONSUMED',
      entityType: 'LinkTransitionException',
      entityId: draft.consumes.id,
      tenantId: tenantIdOf(scope),
    });
  }
  audit(ctx, {
    eventType: 'PPL.LINK_CREATED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: draft.link.tenantId,
    after: {
      linkId: draft.link.id,
      linkType: draft.link.linkType,
      functionCode: draft.link.functionCode,
    },
  });
  return draft.link;
}

// ---- Pessoas: lista, criação, leitura, edição, leitura sensível ----

function listPeople(request: Request, ctx: PeopleRouteContext, scope: Scope): Response {
  need(ctx, scope, readPermission(scope));
  const url = new URL(request.url);
  const q = (url.searchParams.get('q') ?? '').trim().toLowerCase();
  const status = url.searchParams.get('status');
  const linkType = url.searchParams.get('linkType');
  const functionCode = url.searchParams.get('function');
  const legalEntityId = url.searchParams.get('legalEntityId');
  const page = Math.max(1, Number(url.searchParams.get('page') ?? '1') || 1);
  const pageSize =
    Number(url.searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE;
  if (pageSize > MAX_PAGE_SIZE) {
    throw new FakeApiError(422, 'PLT_PAGE_LIMIT_EXCEEDED');
  }
  const matching = ctx.state.people
    .filter(
      (person) => scope.kind === 'network' || (person.visibility[scope.tenant.id] ?? []).length > 0,
    )
    .filter((person) => !status || person.status === status)
    .filter(
      (person) =>
        !q ||
        displayNameOf(person).toLowerCase().includes(q) ||
        `${person.givenName} ${person.familyName}`.toLowerCase().includes(q),
    )
    .filter((person) => {
      if (!linkType && !functionCode && !legalEntityId) {
        return true;
      }
      // O mesmo vínculo (entre os visíveis ao escopo) satisfaz todos os filtros — sem canal lateral (REQ-PPL-015).
      return visibleLinks(ctx, scope, person).some(
        (link) =>
          (!linkType || link.linkType === linkType) &&
          (!functionCode || link.functionCode === functionCode) &&
          (!legalEntityId || link.legalEntityId === legalEntityId),
      );
    })
    .sort((a, b) => displayNameOf(a).localeCompare(displayNameOf(b), 'pt-BR'));
  const items = matching.slice((page - 1) * pageSize, page * pageSize).map(toPersonSummary);
  return ok({ items, page, pageSize, total: matching.length });
}

async function createPerson(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
): Promise<Response> {
  need(ctx, scope, managePermission(scope));
  const body = await readJsonBody(request);
  const normalized = normalizePerson(ctx, body, true);
  const linkBody = record(body)['link'];
  if (scope.kind === 'tenant' && !has(body, 'link')) {
    throw validation([fieldError('link', 'required')]);
  }
  if (
    normalized.federalCode !== null &&
    findDuplicate(ctx, normalized.country, normalized.federalCode)
  ) {
    throw new FakeApiError(409, 'PPL_DUPLICATE_PERSON');
  }
  const now = new Date().toISOString();
  const id = fakePeopleId(ctx.state);
  const draft = has(body, 'link') ? buildLink(ctx, scope, id, [], linkBody, 'link.') : null;
  const person: FakePerson = {
    id,
    givenName: normalized.givenName,
    familyName: normalized.familyName,
    socialName: normalized.socialName,
    country: normalized.country,
    federalCode: normalized.federalCode,
    birthDate: normalized.birthDate,
    preferredLocale: normalized.preferredLocale,
    photoDocumentId: null,
    status: 'Active',
    contacts: normalized.contacts,
    createdAt: now,
    modifiedAt: null,
    version: 1,
    visibility: {},
  };
  ctx.state.people.push(person);
  const link = draft ? commitLink(ctx, scope, person, draft) : null;
  audit(ctx, {
    eventType: 'PPL.PERSON_CREATED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: tenantIdOf(scope),
    after: { country: person.country },
  });
  const detail = toPersonDetail(person, visibilityOf(scope, person));
  return created(
    {
      ...detail,
      federalCodeMasked: maskFederalCode(person.country, person.federalCode ?? ''),
      link: link ? toLinkResponse(link, todayIso()) : null,
    },
    etagOf(person.version),
  );
}

function getPerson(ctx: PeopleRouteContext, scope: Scope, personId: string): Response {
  need(ctx, scope, readPermission(scope));
  const person = visiblePerson(ctx, scope, personId);
  return ok(toPersonDetail(person, visibilityOf(scope, person)), etagOf(person.version));
}

async function updatePerson(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Promise<Response> {
  need(ctx, scope, managePermission(scope));
  const person = visiblePerson(ctx, scope, personId);
  requireIfMatch(request, etagOf(person.version));
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const errors: FieldErrorItem[] = [];
  const givenName = str(body, 'givenName');
  const familyName = str(body, 'familyName');
  if (givenName === null) {
    errors.push(fieldError('givenName', 'required'));
  }
  if (familyName === null) {
    errors.push(fieldError('familyName', 'required'));
  }
  const locale = str(body, 'preferredLocale') ?? person.preferredLocale;
  if (!LOCALES.has(locale)) {
    errors.push(fieldError('preferredLocale', 'invalid'));
  }
  const status = str(body, 'status') ?? person.status;
  if (status !== 'Active' && status !== 'Inactive') {
    errors.push(fieldError('status', 'invalid'));
  }
  const photo = str(body, 'photoDocumentId');
  if (photo !== null && !UUID.test(photo)) {
    errors.push(fieldError('photoDocumentId', 'invalid'));
  }
  // Identificador fiscal e nascimento só chegam juntos (o cofre cifra os dois na mesma linha).
  const rawFederal = str(body, 'federalCode');
  const rawBirth = str(body, 'birthDate');
  let federalCode = person.federalCode;
  let birthDate = person.birthDate;
  // O país só muda enquanto a pessoa é pendente (sem cofre) e junto com o identificador e o nascimento (OPEN-100).
  const rawCountry = str(body, 'country')?.toUpperCase() ?? null;
  const changesCountry = rawCountry !== null && rawCountry !== person.country;
  if (changesCountry && person.federalCode !== null) {
    errors.push(fieldError('country', 'immutable'));
  }
  if (rawFederal !== null || rawBirth !== null || changesCountry) {
    if (rawFederal === null) {
      errors.push(fieldError('federalCode', 'required'));
    }
    if (rawBirth === null) {
      errors.push(fieldError('birthDate', 'required'));
    }
  }
  failIf(errors);
  const country = changesCountry ? rawCountry : person.country;
  if (rawFederal !== null && rawBirth !== null) {
    const normalized = normalizePerson(
      ctx,
      {
        givenName,
        familyName,
        country,
        federalCode: rawFederal,
        birthDate: rawBirth,
      },
      true,
    );
    if (
      normalized.federalCode !== null &&
      findDuplicate(ctx, country, normalized.federalCode, person.id)
    ) {
      throw new FakeApiError(409, 'PPL_DUPLICATE_PERSON');
    }
    federalCode = normalized.federalCode;
    birthDate = normalized.birthDate;
  }
  const contacts = has(body, 'contacts')
    ? normalizeContacts(ctx, record(body)['contacts'], 'contacts')
    : null;
  person.givenName = givenName ?? person.givenName;
  person.familyName = familyName ?? person.familyName;
  person.socialName = str(body, 'socialName');
  person.preferredLocale = locale;
  person.status = status === 'Inactive' ? 'Inactive' : 'Active';
  person.photoDocumentId = photo;
  person.country = country;
  person.federalCode = federalCode;
  person.birthDate = birthDate;
  if (contacts) {
    person.contacts = contacts;
  }
  bump(person);
  audit(ctx, {
    eventType: 'PPL.PERSON_UPDATED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: tenantIdOf(scope),
    reason,
  });
  return ok(toPersonDetail(person, visibilityOf(scope, person)), etagOf(person.version));
}

function getSensitive(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Response {
  need(ctx, scope, 'person.sensitive.read');
  const person = visiblePerson(ctx, scope, personId);
  const purpose = requirePurpose(request);
  if (person.federalCode === null || person.birthDate === null) {
    throw new FakeApiError(422, 'PPL_PERSON_PENDING');
  }
  audit(ctx, {
    eventType: 'PPL.SENSITIVE_READ',
    entityType: 'Person',
    entityId: person.id,
    tenantId: tenantIdOf(scope),
    purpose,
    permission: 'person.sensitive.read',
  });
  return ok({
    id: person.id,
    country: person.country,
    federalCode: formatFederalCode(person.country, person.federalCode),
    birthDate: person.birthDate,
  });
}

// ---- Vínculos ----

function listLinks(ctx: PeopleRouteContext, scope: Scope, personId: string): Response {
  need(ctx, scope, 'person.link.read');
  const person = visiblePerson(ctx, scope, personId);
  const today = todayIso();
  return ok(visibleLinks(ctx, scope, person).map((link) => toLinkResponse(link, today)));
}

function getLink(
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  linkId: string,
): Response {
  need(ctx, scope, 'person.link.read');
  const person = visiblePerson(ctx, scope, personId);
  const link = visibleLink(ctx, scope, person, linkId);
  return ok(toLinkResponse(link, todayIso()), etagOf(link.version));
}

async function postLink(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Promise<Response> {
  need(ctx, scope, 'person.link.manage');
  const person = visiblePerson(ctx, scope, personId);
  const body = await readJsonBody(request);
  const draft = buildLink(
    ctx,
    scope,
    person.id,
    ctx.state.links.filter((link) => link.personId === person.id),
    body,
  );
  const link = commitLink(ctx, scope, person, draft);
  return created(toLinkResponse(link, todayIso()), etagOf(link.version));
}

async function putLink(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  linkId: string,
): Promise<Response> {
  need(ctx, scope, 'person.link.manage');
  const person = visiblePerson(ctx, scope, personId);
  const link = visibleLink(ctx, scope, person, linkId);
  requireIfMatch(request, etagOf(link.version));
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const errors: FieldErrorItem[] = [];
  const tenant = ctx.state.tenants.find((item) => item.id === link.tenantId) ?? null;
  const primarySiteId = str(body, 'primarySiteId');
  const additionalRaw = record(body)['additionalSiteIds'];
  const additionalSiteIds = Array.isArray(additionalRaw)
    ? additionalRaw.filter((item): item is string => typeof item === 'string')
    : [];
  errors.push(...siteErrors(tenant, true, primarySiteId, additionalSiteIds, ''));
  const justification = str(body, 'classificationJustification');
  if (justification !== null && justification.length > 2000) {
    errors.push(fieldError('classificationJustification', 'too_long'));
  }
  for (const name of ['legalOpinionDocumentId', 'contractDocumentId']) {
    const value = str(body, name);
    if (value !== null && !UUID.test(value)) {
      errors.push(fieldError(name, 'invalid'));
    }
  }
  failIf(errors);
  const opinion = str(body, 'legalOpinionDocumentId');
  link.primarySiteId = primarySiteId;
  link.additionalSiteIds = additionalSiteIds;
  link.classificationJustification = justification;
  link.legalOpinionDocumentId = opinion;
  link.contractDocumentId = str(body, 'contractDocumentId');
  // Parecer anexado a um enquadramento "caso a caso" → `Valid`.
  if (link.classificationStatus === 'PendingOpinion' && opinion !== null) {
    link.classificationStatus = 'Valid';
  }
  link.version += 1;
  link.modifiedAt = new Date().toISOString();
  audit(ctx, {
    eventType: 'PPL.LINK_UPDATED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: link.tenantId,
    reason,
  });
  return ok(toLinkResponse(link, todayIso()), etagOf(link.version));
}

function startOffboarding(ctx: PeopleRouteContext, link: FakeLink): void {
  if (ctx.state.offboarding.some((item) => item.personLinkId === link.id)) {
    return;
  }
  ctx.state.offboarding.push({
    id: fakePeopleId(ctx.state),
    personId: link.personId,
    personLinkId: link.id,
    items: buildOffboardingItems(),
    status: 'InProgress',
    startedAt: new Date().toISOString(),
    accessRevokedAt: null,
  });
}

async function endLink(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  linkId: string,
): Promise<Response> {
  need(ctx, scope, 'person.link.manage');
  const person = visiblePerson(ctx, scope, personId);
  const link = visibleLink(ctx, scope, person, linkId);
  const body = await readJsonBody(request);
  const reason = str(body, 'reason');
  const errors: FieldErrorItem[] = [];
  if (reason === null) {
    errors.push(fieldError('reason', 'required'));
  } else if (reason.length > 400) {
    errors.push(fieldError('reason', 'too_long'));
  }
  const rawEnd = str(body, 'endDate');
  const endDate = rawEnd ?? todayIso();
  if (rawEnd !== null && (!isIsoDate(rawEnd) || rawEnd < link.effectiveFrom)) {
    errors.push(fieldError('endDate', 'invalid'));
  }
  failIf(errors);
  if (link.endedAt !== null) {
    throw new FakeApiError(409, 'PPL_LINK_NOT_ACTIVE');
  }
  link.effectiveTo = endDate;
  link.endedAt = new Date().toISOString();
  link.endedBy = ctx.me.user.id;
  link.endReason = reason;
  link.version += 1;
  link.modifiedAt = link.endedAt;
  startOffboarding(ctx, link);
  audit(ctx, {
    eventType: 'PPL.LINK_ENDED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: link.tenantId,
    reason: { code: 'PPL_LINK_ENDED', text: reason },
  });
  return ok(toLinkResponse(link, todayIso()), etagOf(link.version));
}

// ---- Exceções de transição CLT → flexível (rede; duas chaves) ----

function openApproval(
  ctx: PeopleRouteContext,
  actionType: string,
  objectType: string,
  objectId: string,
  requiredSecondPermission: string,
): FakeApproval {
  const now = new Date().toISOString();
  const approval: FakeApproval = {
    id: fakePeopleId(ctx.state),
    actionType,
    objectType,
    objectId,
    status: 'PendingSecondKey',
    firstActorUserId: ctx.me.user.id,
    firstAt: now,
    expiresAt: shiftHours(now, TWO_KEY_HOURS),
    secondActorUserId: null,
    secondAt: null,
    requiredSecondPermission,
    requireDistinctRole: true,
    firstActorRoles: [...ctx.me.networkAccess.roles],
  };
  ctx.state.approvals.push(approval);
  return approval;
}

function exceptionOf(ctx: PeopleRouteContext, personId: string, id: string): FakeException {
  const found = ctx.state.exceptions.find((item) => item.id === id && item.personId === personId);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function toExceptionResponse(item: FakeException) {
  const { version: _version, ...rest } = item;
  return rest;
}

async function requestException(
  request: Request,
  ctx: PeopleRouteContext,
  personId: string,
): Promise<Response> {
  const scope: Scope = { kind: 'network' };
  need(ctx, scope, 'person.link.exception.request');
  requireStepUp(ctx);
  const person = visiblePerson(ctx, scope, personId);
  const body = await readJsonBody(request);
  const errors: FieldErrorItem[] = [];
  const today = todayIso();
  const fromLinkId = str(body, 'fromLinkId');
  const description = str(body, 'scopeChangeDescription');
  const opinion = str(body, 'legalOpinionDocumentId');
  let source: FakeLink | undefined;
  if (fromLinkId === null) {
    errors.push(fieldError('fromLinkId', 'required'));
  } else if (!UUID.test(fromLinkId)) {
    errors.push(fieldError('fromLinkId', 'invalid'));
  } else {
    // Como o servidor: vínculo inexistente é `unknown`; existente que não é CLT encerrado é `not_an_ended_clt_link`.
    const found = ctx.state.links.find(
      (link) => link.id === fromLinkId && link.personId === person.id,
    );
    if (!found) {
      errors.push(fieldError('fromLinkId', 'unknown'));
    } else if (
      found.linkType !== 'CLT' ||
      !['Ended', 'Expired'].includes(linkStatusOn(found, today))
    ) {
      errors.push(fieldError('fromLinkId', 'not_an_ended_clt_link'));
    } else {
      source = found;
    }
  }
  if (description === null) {
    errors.push(fieldError('scopeChangeDescription', 'required'));
  }
  if (opinion === null) {
    errors.push(fieldError('legalOpinionDocumentId', 'required'));
  } else if (!UUID.test(opinion)) {
    errors.push(fieldError('legalOpinionDocumentId', 'invalid'));
  }
  failIf(errors);
  if (!source || description === null || opinion === null) {
    throw validation([fieldError('fromLinkId', 'unknown')]);
  }
  const ended = source.effectiveTo ?? today;
  if (daysBetween(ended, today) < ctx.state.settings.minIntervalDays) {
    // Como o servidor: o código de negócio da trava + o campo (`interval_not_elapsed`) em `errors[]`.
    throw new FakeApiError(422, 'PPL_TRANSITION_INTERVAL_NOT_ELAPSED', {
      errors: [fieldError('fromLinkId', 'interval_not_elapsed')],
    });
  }
  if (
    ctx.state.exceptions.some(
      (item) =>
        item.fromLinkId === source.id && ['PendingSecondKey', 'Approved'].includes(item.status),
    )
  ) {
    throw new FakeApiError(409, 'PPL_TRANSITION_EXCEPTION_EXISTS');
  }
  const exceptionId = fakePeopleId(ctx.state);
  const approval = openApproval(
    ctx,
    'people.link.transition-exception',
    'LinkTransitionException',
    exceptionId,
    'person.link.exception.request',
  );
  const exception: FakeException = {
    id: exceptionId,
    personId: person.id,
    fromLinkId: source.id,
    legalEntityId: source.legalEntityId,
    functionCode: source.functionCode,
    scopeChangeDescription: description,
    legalOpinionDocumentId: opinion,
    status: 'PendingSecondKey',
    requestedBy: ctx.me.user.id,
    secondKeyBy: null,
    twoKeyApprovalId: approval.id,
    approvedAt: null,
    consumedAt: null,
    consumedByLinkId: null,
    createdAt: new Date().toISOString(),
    version: 1,
  };
  ctx.state.exceptions.push(exception);
  audit(ctx, {
    eventType: 'PPL.LINK_TRANSITION_EXCEPTION_REQUESTED',
    entityType: 'LinkTransitionException',
    entityId: exception.id,
    permission: 'person.link.exception.request',
  });
  return jsonResponse(toExceptionResponse(exception), 202, { ETag: etagOf(exception.version) });
}

// ---- Funções e Matriz de Enquadramento ----

function listFunctions(request: Request, ctx: PeopleRouteContext, scope: Scope): Response {
  need(ctx, scope, 'person.link.read');
  const status = new URL(request.url).searchParams.get('status') ?? 'Active';
  return ok(FUNCTIONS.filter((item) => item.status === status));
}

/** Referência de pessoas jurídicas (OPEN-095): a rede vê a holding e os centros; o tenant, só o próprio centro. */
function listLegalEntities(ctx: PeopleRouteContext, scope: Scope): Response {
  need(ctx, scope, 'person.link.read');
  const centers = ctx.state.tenants
    .filter((tenant) => scope.kind === 'network' || tenant.id === scope.tenant.id)
    .map((tenant) => ({
      id: tenant.legalEntityId,
      kind: 'Center',
      tenantId: tenant.id,
      code: tenant.code,
      name: tenant.legalName,
    }));
  const holding =
    scope.kind === 'network'
      ? [
          {
            id: HOLDING_LEGAL_ENTITY_ID,
            kind: 'Holding',
            tenantId: null,
            code: null,
            name: HOLDING_LEGAL_NAME,
          },
        ]
      : [];
  return ok([...holding, ...centers]);
}

function getCurrentMatrix(ctx: PeopleRouteContext, scope: Scope): Response {
  need(ctx, scope, scope.kind === 'network' ? 'people.matrix.read' : 'person.link.read');
  const current = publishedMatrix(ctx.state);
  if (!current) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return ok(toMatrixResponse(current), etagOf(current.rowVersion));
}

function matrixVersionById(ctx: PeopleRouteContext, id: string): FakeMatrixVersion {
  const found = ctx.state.matrixVersions.find((item) => item.id === id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

async function proposeMatrix(request: Request, ctx: PeopleRouteContext): Promise<Response> {
  const scope: Scope = { kind: 'network' };
  need(ctx, scope, 'people.matrix.manage');
  requireStepUp(ctx);
  const current = publishedMatrix(ctx.state);
  if (!current) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  requireIfMatch(request, etagOf(current.rowVersion));
  const body = await readJsonBody(request);
  const opinion = str(body, 'opinionDocumentId');
  if (opinion === null) {
    throw new FakeApiError(422, 'PPL_MATRIX_OPINION_REQUIRED');
  }
  const errors: FieldErrorItem[] = [];
  if (!UUID.test(opinion)) {
    errors.push(fieldError('opinionDocumentId', 'invalid'));
  }
  const summary = str(body, 'changeSummary');
  if (summary === null) {
    errors.push(fieldError('changeSummary', 'required'));
  } else if (summary.length > 1000) {
    errors.push(fieldError('changeSummary', 'too_long'));
  }
  const rawFrom = str(body, 'effectiveFrom');
  if (rawFrom !== null && !isIsoDate(rawFrom)) {
    errors.push(fieldError('effectiveFrom', 'invalid'));
  } else if (rawFrom !== null && rawFrom < todayIso()) {
    errors.push(fieldError('effectiveFrom', 'in_the_past')); // hoje, futura (agendada) ou omitida — OPEN-097
  }
  const rawEntries = record(body)['entries'];
  const entries = Array.isArray(rawEntries) ? rawEntries : [];
  const expected = FUNCTIONS.length * LINK_TYPE_LIST.length;
  const seen = new Set<string>();
  let wellFormed = entries.length === expected;
  for (const entry of entries) {
    const functionCode = str(entry, 'functionCode') ?? '';
    const linkType = str(entry, 'linkType') ?? '';
    const indication = str(entry, 'indication') ?? '';
    const key = `${functionCode}:${linkType}`;
    if (
      !FUNCTION_CODES.has(functionCode) ||
      !LINK_TYPE_LIST.some((item) => item === linkType) ||
      !['Indicated', 'CaseByCase', 'NotAllowed'].includes(indication) ||
      seen.has(key)
    ) {
      wellFormed = false;
    }
    seen.add(key);
  }
  if (!wellFormed || seen.size !== expected) {
    errors.push(fieldError('entries', 'incomplete'));
  }
  failIf(errors);
  if (ctx.state.matrixVersions.some((item) => item.status === 'PendingSecondKey')) {
    throw new FakeApiError(409, 'PPL_MATRIX_PENDING');
  }
  // Aprovada com vigência futura (OPEN-097): uma nova proposta espera ela entrar em vigor (OPEN-104), como o servidor.
  if (
    ctx.state.matrixVersions.some(
      (item) =>
        item.status === 'Published' &&
        item.effectiveFrom !== null &&
        item.effectiveFrom > todayIso(),
    )
  ) {
    throw new FakeApiError(409, 'PPL_MATRIX_SCHEDULED');
  }
  const versionId = fakePeopleId(ctx.state);
  const approval = openApproval(
    ctx,
    'people.matrix.manage',
    'FunctionLinkMatrixVersion',
    versionId,
    'people.matrix.manage',
  );
  const next: FakeMatrixVersion = {
    id: versionId,
    version: Math.max(...ctx.state.matrixVersions.map((item) => item.version)) + 1,
    rowVersion: 1,
    status: 'PendingSecondKey',
    changeSummary: summary ?? '',
    effectiveFrom: rawFrom,
    opinionDocumentId: opinion,
    requestedBy: ctx.me.user.id,
    secondKeyBy: null,
    twoKeyApprovalId: approval.id,
    publishedAt: null,
    supersededAt: null,
    createdAt: new Date().toISOString(),
    entries: entries.map((entry) => ({
      functionCode: str(entry, 'functionCode') ?? '',
      linkType: str(entry, 'linkType') ?? '',
      indication: str(entry, 'indication') ?? '',
    })),
  };
  ctx.state.matrixVersions.push(next);
  audit(ctx, {
    eventType: 'PPL.FUNCTION_LINK_MATRIX_VERSION_CREATED',
    entityType: 'FunctionLinkMatrixVersion',
    entityId: next.id,
    permission: 'people.matrix.manage',
  });
  return jsonResponse(toMatrixResponse(next), 202, { ETag: etagOf(next.rowVersion) });
}

/** Segunda chave genérica: só as aprovações de pessoas; as demais seguem para a Autorização (`null`). */
function secondKey(ctx: PeopleRouteContext, approvalId: string): Response | null {
  const approval = ctx.state.approvals.find((item) => item.id === approvalId);
  if (!approval) {
    return null;
  }
  requireStepUp(ctx);
  if (approval.status !== 'PendingSecondKey') {
    throw new FakeApiError(409, 'AUTHZ_TWO_KEY_NOT_PENDING');
  }
  if (approval.expiresAt < new Date().toISOString()) {
    throw new FakeApiError(409, 'AUTHZ_TWO_KEY_EXPIRED');
  }
  if (approval.firstActorUserId === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_TWO_KEY_SAME_ACTOR');
  }
  const permissions = networkPermissions(ctx);
  if (!permissions.has(approval.requiredSecondPermission)) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
  const roles = ctx.me.networkAccess.roles;
  if (
    approval.requireDistinctRole &&
    roles.every((role) => approval.firstActorRoles.includes(role))
  ) {
    throw new FakeApiError(422, 'AUTHZ_TWO_KEY_DISTINCT_ROLE_REQUIRED');
  }
  const now = new Date().toISOString();
  approval.status = 'Completed';
  approval.secondActorUserId = ctx.me.user.id;
  approval.secondAt = now;
  if (approval.actionType === 'people.matrix.manage') {
    applyMatrixPublish(ctx, approval.objectId, now);
  } else {
    const exception = ctx.state.exceptions.find((item) => item.id === approval.objectId);
    if (exception) {
      exception.status = 'Approved';
      exception.approvedAt = now;
      exception.secondKeyBy = ctx.me.user.id;
      exception.version += 1;
      audit(ctx, {
        eventType: 'PPL.LINK_TRANSITION_EXCEPTION_APPROVED',
        entityType: 'LinkTransitionException',
        entityId: exception.id,
        permission: approval.requiredSecondPermission,
      });
    }
  }
  return ok(toApprovalResponse(approval));
}

function applyMatrixPublish(ctx: PeopleRouteContext, versionId: string, now: string): void {
  const version = matrixVersionById(ctx, versionId);
  // A vigência pedida vale se for hoje ou futura (agendada); no passado, é puxada para o dia da segunda chave. Só uma vigência imediata supera já as anteriores — a
  // agendada deixa a atual em vigor até a data (o job diário do servidor as supera então; aqui a leitura por data basta).
  const today = todayIso();
  const effectiveFrom =
    version.effectiveFrom !== null && version.effectiveFrom > today ? version.effectiveFrom : today;
  if (effectiveFrom <= today) {
    for (const item of ctx.state.matrixVersions) {
      if (
        item.status === 'Published' &&
        item.effectiveFrom !== null &&
        item.effectiveFrom <= effectiveFrom
      ) {
        item.status = 'Superseded';
        item.supersededAt = now;
        item.rowVersion += 1;
      }
    }
  }
  version.status = 'Published';
  version.publishedAt = now;
  version.secondKeyBy = ctx.me.user.id;
  version.effectiveFrom = effectiveFrom;
  version.rowVersion += 1;
  audit(ctx, {
    eventType: 'PPL.MATRIX_CHANGED',
    entityType: 'FunctionLinkMatrixVersion',
    entityId: version.id,
    reason: { code: 'PPL_MATRIX_PUBLISHED', text: null },
    permission: 'people.matrix.manage',
  });
}

// ---- Licenças ----

function todayAlerts(ctx: PeopleRouteContext): { today: string; alertDays: readonly number[] } {
  return { today: todayIso(), alertDays: ctx.state.settings.alertDays };
}

function toLicenseResponse(ctx: PeopleRouteContext, item: FakeLicense) {
  const { version: _version, ...rest } = item;
  const { today, alertDays } = todayAlerts(ctx);
  return { ...rest, status: credentialStatus(item.validUntil, today, alertDays) };
}

function normalizeLicense(body: unknown) {
  const errors: FieldErrorItem[] = [];
  const council = str(body, 'council');
  if (council === null) {
    errors.push(fieldError('council', 'required'));
  } else if (!BOARDS.some((board) => board.code === council)) {
    errors.push(fieldError('council', 'unknown'));
  }
  // UF: obrigatória em conselho do Brasil; opcional fora dele e, se informada, de 2 letras (OPEN-100).
  const state = (str(body, 'state') ?? '').toUpperCase();
  const board = BOARDS.find((item) => item.code === council);
  if (state === '' ? board === undefined || board.country === 'BR' : !/^[A-Z]{2}$/.test(state)) {
    errors.push(fieldError('state', state === '' ? 'required' : 'invalid'));
  }
  const number = str(body, 'number');
  if (number === null) {
    errors.push(fieldError('number', 'required'));
  } else if (number.length > 64) {
    errors.push(fieldError('number', 'too_long'));
  }
  const validUntil = str(body, 'validUntil');
  checkDate(errors, 'validUntil', validUntil);
  const documentId = str(body, 'documentId');
  if (documentId !== null && !UUID.test(documentId)) {
    errors.push(fieldError('documentId', 'invalid'));
  }
  failIf(errors);
  return {
    council: council ?? '',
    state: state === '' ? null : state,
    number: number ?? '',
    validUntil: validUntil ?? '',
    documentId,
  };
}

function licenseOf(ctx: PeopleRouteContext, person: FakePerson, id: string): FakeLicense {
  const found = ctx.state.licenses.find((item) => item.id === id && item.personId === person.id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

async function postLicense(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.manage');
  const person = visiblePerson(ctx, scope, personId);
  const input = normalizeLicense(await readJsonBody(request));
  const license: FakeLicense = {
    id: fakePeopleId(ctx.state),
    personId: person.id,
    ...input,
    status: 'Valid',
    createdAt: new Date().toISOString(),
    modifiedAt: null,
    version: 1,
  };
  ctx.state.licenses.push(license);
  audit(ctx, {
    eventType: 'PPL.LICENSE_SAVED',
    entityType: 'ProfessionalLicense',
    entityId: license.id,
    tenantId: tenantIdOf(scope),
  });
  return created(toLicenseResponse(ctx, license), etagOf(license.version));
}

async function putLicense(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  licenseId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.manage');
  const person = visiblePerson(ctx, scope, personId);
  const license = licenseOf(ctx, person, licenseId);
  requireIfMatch(request, etagOf(license.version));
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const input = normalizeLicense(body);
  Object.assign(license, input);
  license.version += 1;
  license.modifiedAt = new Date().toISOString();
  audit(ctx, {
    eventType: 'PPL.LICENSE_SAVED',
    entityType: 'ProfessionalLicense',
    entityId: license.id,
    tenantId: tenantIdOf(scope),
    reason,
  });
  return ok(toLicenseResponse(ctx, license), etagOf(license.version));
}

// ---- Certificações ----

function toCertificationResponse(ctx: PeopleRouteContext, item: FakeCertification) {
  const { version: _version, ...rest } = item;
  const { today, alertDays } = todayAlerts(ctx);
  return {
    ...rest,
    status:
      item.status === 'Revoked' ? 'Revoked' : credentialStatus(item.validUntil, today, alertDays),
  };
}

function normalizeCertification(body: unknown) {
  const errors: FieldErrorItem[] = [];
  const typeCode = str(body, 'typeCode');
  const type = CERTIFICATION_TYPES.find((item) => item.code === typeCode);
  if (typeCode === null) {
    errors.push(fieldError('typeCode', 'required'));
  } else if (!type) {
    errors.push(fieldError('typeCode', 'unknown'));
  }
  const studyId = str(body, 'studyId');
  if (type?.scope === 'Study' && studyId === null) {
    errors.push(fieldError('studyId', 'study_scope_required'));
  }
  if (type?.scope === 'Network' && studyId !== null) {
    errors.push(fieldError('studyId', 'study_scope_not_allowed'));
  }
  if (studyId !== null && !UUID.test(studyId)) {
    errors.push(fieldError('studyId', 'invalid'));
  }
  const issuedAt = str(body, 'issuedAt');
  const validUntil = str(body, 'validUntil');
  checkDate(errors, 'issuedAt', issuedAt);
  checkDate(errors, 'validUntil', validUntil);
  if (isIsoDate(validUntil) && isIsoDate(issuedAt) && validUntil < issuedAt) {
    errors.push(fieldError('validUntil', 'invalid'));
  }
  const evidence = str(body, 'evidenceDocumentId');
  if (evidence === null && type?.requiresEvidence) {
    errors.push(fieldError('evidenceDocumentId', 'evidence_required'));
  } else if (evidence !== null && !UUID.test(evidence)) {
    errors.push(fieldError('evidenceDocumentId', 'invalid'));
  }
  const origin = str(body, 'origin') ?? 'Manual';
  if (origin !== 'Manual' && origin !== 'Lms') {
    errors.push(fieldError('origin', 'invalid'));
  }
  failIf(errors);
  return {
    typeCode: typeCode ?? '',
    studyId,
    issuedAt: issuedAt ?? '',
    validUntil: validUntil ?? '',
    evidenceDocumentId: evidence,
    origin,
  };
}

function certificationOf(
  ctx: PeopleRouteContext,
  person: FakePerson,
  id: string,
): FakeCertification {
  const found = ctx.state.certifications.find(
    (item) => item.id === id && item.personId === person.id,
  );
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

async function postCertification(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.manage');
  const person = visiblePerson(ctx, scope, personId);
  const input = normalizeCertification(await readJsonBody(request));
  const certification: FakeCertification = {
    id: fakePeopleId(ctx.state),
    personId: person.id,
    ...input,
    status: 'Valid',
    verifiedAt: null,
    verifiedBy: null,
    revokedAt: null,
    createdBy: ctx.me.user.id,
    createdAt: new Date().toISOString(),
    modifiedAt: null,
    version: 1,
  };
  ctx.state.certifications.push(certification);
  audit(ctx, {
    eventType: 'PPL.CERTIFICATION_SAVED',
    entityType: 'Certification',
    entityId: certification.id,
    tenantId: tenantIdOf(scope),
  });
  return created(toCertificationResponse(ctx, certification), etagOf(certification.version));
}

async function putCertification(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  certificationId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.manage');
  const person = visiblePerson(ctx, scope, personId);
  const certification = certificationOf(ctx, person, certificationId);
  requireIfMatch(request, etagOf(certification.version));
  if (certification.status === 'Revoked') {
    throw new FakeApiError(409, 'PPL_CERTIFICATION_REVOKED');
  }
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const input = normalizeCertification(body);
  if (input.typeCode !== certification.typeCode) {
    throw validation([fieldError('typeCode', 'immutable')]);
  }
  const { today, alertDays } = todayAlerts(ctx);
  const wasLapsing = credentialStatus(certification.validUntil, today, alertDays) !== 'Valid';
  const reissued = input.validUntil !== certification.validUntil;
  Object.assign(certification, input);
  // Reemissão que volta a `Valid` perde a verificação anterior: registra-se de novo e outra pessoa verifica de novo.
  if (wasLapsing && reissued && credentialStatus(input.validUntil, today, alertDays) === 'Valid') {
    certification.verifiedAt = null;
    certification.verifiedBy = null;
  }
  certification.version += 1;
  certification.modifiedAt = new Date().toISOString();
  audit(ctx, {
    eventType: 'PPL.CERTIFICATION_SAVED',
    entityType: 'Certification',
    entityId: certification.id,
    tenantId: tenantIdOf(scope),
    reason,
  });
  return ok(toCertificationResponse(ctx, certification), etagOf(certification.version));
}

/** SoD por ato (regra 7 da Parte 0.3): quem registrou nunca verifica (`422 AUTHZ_SOD_VIOLATION`). */
function verifyCertification(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  certificationId: string,
): Response {
  need(ctx, scope, 'person.credential.verify');
  const person = visiblePerson(ctx, scope, personId);
  const certification = certificationOf(ctx, person, certificationId);
  requireIfMatch(request, etagOf(certification.version)); // o verificador confirma a versão que viu (OPEN-099)
  if (certification.status === 'Revoked') {
    throw new FakeApiError(409, 'PPL_CERTIFICATION_REVOKED');
  }
  if (certification.createdBy === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
  }
  certification.verifiedBy = ctx.me.user.id;
  certification.verifiedAt = new Date().toISOString();
  certification.version += 1;
  certification.modifiedAt = certification.verifiedAt;
  audit(ctx, {
    eventType: 'PPL.CERTIFICATION_VERIFIED',
    entityType: 'Certification',
    entityId: certification.id,
    tenantId: tenantIdOf(scope),
    reason: { code: 'PPL_CERTIFICATION_VERIFIED', text: null },
    permission: 'person.credential.verify',
  });
  return ok(toCertificationResponse(ctx, certification), etagOf(certification.version));
}

/** Revogação por Qualidade (OPEN-099): `person.credential.revoke`, step-up, `If-Match` e razão; irreversível (`409 PPL_CERTIFICATION_REVOKED`). */
async function revokeCertification(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  certificationId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.revoke');
  requireStepUp(ctx);
  const person = visiblePerson(ctx, scope, personId);
  const certification = certificationOf(ctx, person, certificationId);
  requireIfMatch(request, etagOf(certification.version));
  const reason = requireReason(await readJsonBody(request));
  if (certification.status === 'Revoked') {
    throw new FakeApiError(409, 'PPL_CERTIFICATION_REVOKED');
  }
  certification.status = 'Revoked';
  certification.revokedAt = new Date().toISOString();
  certification.version += 1;
  certification.modifiedAt = certification.revokedAt;
  audit(ctx, {
    eventType: 'PPL.CERTIFICATION_REVOKED',
    entityType: 'Certification',
    entityId: certification.id,
    tenantId: tenantIdOf(scope),
    reason,
    permission: 'person.credential.revoke',
  });
  return ok(toCertificationResponse(ctx, certification), etagOf(certification.version));
}

// ---- Capacidades ----

function listSkills(ctx: PeopleRouteContext, scope: Scope, personId: string): Response {
  need(ctx, scope, 'person.credential.read');
  const person = visiblePerson(ctx, scope, personId);
  return ok(ctx.state.skills.filter((item) => item.personId === person.id));
}

async function putSkills(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
): Promise<Response> {
  need(ctx, scope, 'person.credential.manage');
  const person = visiblePerson(ctx, scope, personId);
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const raw = record(body)['skills'];
  const errors: FieldErrorItem[] = [];
  if (!Array.isArray(raw)) {
    throw validation([fieldError('skills', 'required')]);
  }
  const inputs = raw.map((entry: unknown, index) => {
    const code = str(entry, 'skillCode');
    const level = str(entry, 'level');
    const validUntil = str(entry, 'validUntil');
    const evidence = str(entry, 'evidenceDocumentId');
    if (code === null || !SKILLS.some((item) => item.code === code)) {
      errors.push(
        fieldError(`skills[${String(index)}].skillCode`, code === null ? 'required' : 'unknown'),
      );
    }
    if (level !== null && level.length > 100) {
      errors.push(fieldError(`skills[${String(index)}].level`, 'too_long'));
    }
    if (validUntil !== null && !isIsoDate(validUntil)) {
      errors.push(fieldError(`skills[${String(index)}].validUntil`, 'invalid'));
    }
    if (evidence !== null && !UUID.test(evidence)) {
      errors.push(fieldError(`skills[${String(index)}].evidenceDocumentId`, 'invalid'));
    }
    return { code: code ?? '', level, validUntil, evidence };
  });
  failIf(errors);
  // Upsert por código: nunca remove — "perder" uma capacidade é reenviar o código com `validUntil` no passado.
  for (const input of inputs) {
    const existing = ctx.state.skills.find(
      (item) => item.personId === person.id && item.skillCode === input.code,
    );
    if (existing) {
      existing.level = input.level;
      existing.validUntil = input.validUntil;
      existing.evidenceDocumentId = input.evidence;
      existing.modifiedAt = new Date().toISOString();
    } else {
      const row: PersonSkillResponse = {
        id: fakePeopleId(ctx.state),
        personId: person.id,
        skillCode: input.code,
        level: input.level,
        evidenceDocumentId: input.evidence,
        validUntil: input.validUntil,
        createdAt: new Date().toISOString(),
        modifiedAt: null,
      };
      ctx.state.skills.push(row);
    }
  }
  audit(ctx, {
    eventType: 'PPL.SKILL_SAVED',
    entityType: 'Person',
    entityId: person.id,
    tenantId: tenantIdOf(scope),
    reason,
  });
  return ok(ctx.state.skills.filter((item) => item.personId === person.id));
}

// ---- Escala (vínculos fixos) e disponibilidade (declarada) ----

type WeeklyOut = Record<string, { start: string; end: string }[]>;

/** Faixas semanais canônicas: os sete dias na ordem, faixas ordenadas; erros por campo (`weekly.<dia>[i].<campo>`). */
function normalizeWeekly(
  raw: unknown,
  errors: FieldErrorItem[],
  dailyLimitHours: number | null,
): WeeklyOut {
  const result: WeeklyOut = {};
  for (const day of WEEK_DAY_NAMES) {
    result[day] = [];
  }
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    errors.push(fieldError('weekly', 'required'));
    return result;
  }
  const input = raw as Record<string, unknown>;
  for (const [day, windows] of Object.entries(input)) {
    if (!WEEK_DAY_NAMES.includes(day)) {
      errors.push(fieldError(`weekly.${day}`, 'unknown_day'));
      continue;
    }
    if (!Array.isArray(windows)) {
      errors.push(fieldError(`weekly.${day}`, 'invalid'));
      continue;
    }
    const parsed: { start: string; end: string }[] = [];
    windows.forEach((window: unknown, index) => {
      const start = str(window, 'start');
      const end = str(window, 'end');
      const base = `weekly.${day}[${String(index)}]`;
      if (start === null || !TIME.test(start)) {
        errors.push(fieldError(`${base}.start`, 'invalid_time'));
      }
      if (end === null || !TIME.test(end)) {
        errors.push(fieldError(`${base}.end`, 'invalid_time'));
      }
      if (start !== null && end !== null && TIME.test(start) && TIME.test(end)) {
        if (timeToMinutes(start) >= timeToMinutes(end)) {
          errors.push(fieldError(`${base}.end`, 'start_not_before_end'));
        } else {
          parsed.push({ start, end });
        }
      }
    });
    parsed.sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
    for (let index = 1; index < parsed.length; index += 1) {
      if (
        timeToMinutes(parsed[index]?.start ?? '00:00') <
        timeToMinutes(parsed[index - 1]?.end ?? '00:00')
      ) {
        errors.push(fieldError(`weekly.${day}[${String(index)}]`, 'overlap'));
      }
    }
    result[day] = parsed;
    if (dailyLimitHours !== null) {
      const minutes = parsed.reduce(
        (sum, item) => sum + timeToMinutes(item.end) - timeToMinutes(item.start),
        0,
      );
      if (minutes > dailyLimitHours * 60) {
        errors.push(fieldError(`weekly.${day}`, 'exceeds_intern_daily_limit'));
      }
    }
  }
  return result;
}

function linkSites(link: FakeLink): string[] {
  return link.primarySiteId === null
    ? [...link.additionalSiteIds]
    : [link.primarySiteId, ...link.additionalSiteIds];
}

function toScheduleResponse(item: FakeSchedule) {
  const { version: _version, ...rest } = item;
  return rest;
}

function scheduleOf(ctx: PeopleRouteContext, link: FakeLink, id: string): FakeSchedule {
  const found = ctx.state.schedules.find((item) => item.id === id && item.personLinkId === link.id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function normalizeSchedule(
  ctx: PeopleRouteContext,
  link: FakeLink,
  body: unknown,
  ignoreId: string | null,
) {
  const errors: FieldErrorItem[] = [];
  const from = str(body, 'effectiveFrom');
  const to = str(body, 'effectiveTo');
  checkDate(errors, 'effectiveFrom', from);
  if (to !== null && (!isIsoDate(to) || (from !== null && to < from))) {
    errors.push(fieldError('effectiveTo', 'invalid'));
  }
  const siteId = str(body, 'siteId');
  if (siteId === null) {
    errors.push(fieldError('siteId', 'required'));
  } else if (!linkSites(link).includes(siteId)) {
    errors.push(fieldError('siteId', 'not_a_link_site'));
  }
  const weekly = normalizeWeekly(
    record(body)['weekly'],
    errors,
    link.linkType === 'Estagiario' ? ctx.state.settings.internMaxDailyHours : null,
  );
  if (
    isIsoDate(from) &&
    ctx.state.schedules.some(
      (item) =>
        item.personLinkId === link.id &&
        item.id !== ignoreId &&
        rangesOverlap(item.effectiveFrom, item.effectiveTo, from, to),
    )
  ) {
    errors.push(fieldError('effectiveFrom', 'overlap'));
  }
  failIf(errors);
  return { effectiveFrom: from ?? '', effectiveTo: to, siteId: siteId ?? '', weekly };
}

/** Escala só em vínculo fixo e vigente: flexível → 422 PPL_NO_WORK_SCHEDULE_FOR_FLEXIBLE_LINK; encerrado → `link_ended`. */
function requireFixedLink(link: FakeLink): void {
  if (FLEXIBLE_TYPES.has(link.linkType)) {
    throw new FakeApiError(422, 'PPL_NO_WORK_SCHEDULE_FOR_FLEXIBLE_LINK');
  }
  if (link.endedAt !== null) {
    throw validation([fieldError('personLinkId', 'link_ended')]);
  }
}

async function postSchedule(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  linkId: string,
): Promise<Response> {
  need(ctx, scope, 'person.schedule.manage');
  const link = visibleLink(ctx, scope, visiblePerson(ctx, scope, personId), linkId);
  requireFixedLink(link);
  const input = normalizeSchedule(ctx, link, await readJsonBody(request), null);
  const schedule: FakeSchedule = {
    id: fakePeopleId(ctx.state),
    personId: link.personId,
    personLinkId: link.id,
    ...input,
    createdAt: new Date().toISOString(),
    modifiedAt: null,
    version: 1,
  };
  ctx.state.schedules.push(schedule);
  audit(ctx, {
    eventType: 'PPL.SCHEDULE_SAVED',
    entityType: 'WorkSchedule',
    entityId: schedule.id,
    tenantId: link.tenantId,
  });
  return created(toScheduleResponse(schedule), etagOf(schedule.version));
}

async function putSchedule(
  request: Request,
  ctx: PeopleRouteContext,
  scope: Scope,
  personId: string,
  linkId: string,
  scheduleId: string,
): Promise<Response> {
  need(ctx, scope, 'person.schedule.manage');
  const link = visibleLink(ctx, scope, visiblePerson(ctx, scope, personId), linkId);
  const schedule = scheduleOf(ctx, link, scheduleId);
  requireFixedLink(link);
  requireIfMatch(request, etagOf(schedule.version));
  const body = await readJsonBody(request);
  const reason = requireReason(body);
  const input = normalizeSchedule(ctx, link, body, schedule.id);
  if (input.effectiveFrom !== schedule.effectiveFrom) {
    throw validation([fieldError('effectiveFrom', 'immutable')]);
  }
  Object.assign(schedule, input);
  schedule.version += 1;
  schedule.modifiedAt = new Date().toISOString();
  audit(ctx, {
    eventType: 'PPL.SCHEDULE_SAVED',
    entityType: 'WorkSchedule',
    entityId: schedule.id,
    tenantId: link.tenantId,
    reason,
  });
  return ok(toScheduleResponse(schedule), etagOf(schedule.version));
}

function toAvailabilityResponse(item: FakeAvailability) {
  const { version: _version, ...rest } = item;
  return rest;
}

function availabilityOf(ctx: PeopleRouteContext, link: FakeLink, id: string): FakeAvailability {
  const found = ctx.state.availabilities.find(
    (item) => item.id === id && item.personLinkId === link.id,
  );
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function normalizeAvailability(link: FakeLink, body: unknown, currentKind: string | null) {
  const errors: FieldErrorItem[] = [];
  const kind = str(body, 'kind');
  if (kind !== 'Weekly' && kind !== 'Dated') {
    errors.push(fieldError('kind', kind === null ? 'required' : 'invalid'));
  } else if (currentKind !== null && currentKind !== kind) {
    errors.push(fieldError('kind', 'immutable'));
  }
  if (link.endedAt !== null) {
    errors.push(fieldError('personLinkId', 'link_ended'));
  }
  const siteId = str(body, 'siteId');
  if (siteId !== null && !linkSites(link).includes(siteId)) {
    errors.push(fieldError('siteId', 'not_a_link_site'));
  }
  let weekly: WeeklyOut | null = null;
  let date: string | null = null;
  let startTime: string | null = null;
  let endTime: string | null = null;
  if (kind === 'Weekly') {
    weekly = normalizeWeekly(record(body)['weekly'], errors, null);
  } else if (kind === 'Dated') {
    date = str(body, 'date');
    startTime = str(body, 'startTime');
    endTime = str(body, 'endTime');
    checkDate(errors, 'date', date);
    if (startTime === null || !TIME.test(startTime)) {
      errors.push(fieldError('startTime', startTime === null ? 'required' : 'invalid_time'));
    }
    if (endTime === null || !TIME.test(endTime)) {
      errors.push(fieldError('endTime', endTime === null ? 'required' : 'invalid_time'));
    }
    if (
      startTime !== null &&
      endTime !== null &&
      TIME.test(startTime) &&
      TIME.test(endTime) &&
      timeToMinutes(startTime) >= timeToMinutes(endTime)
    ) {
      errors.push(fieldError('endTime', 'start_not_before_end'));
    }
  }
  failIf(errors);
  return { kind: kind ?? 'Weekly', siteId, weekly, date, startTime, endTime };
}

async function postAvailability(
  request: Request,
  ctx: PeopleRouteContext,
  link: FakeLink,
): Promise<Response> {
  const input = normalizeAvailability(link, await readJsonBody(request), null);
  const now = new Date().toISOString();
  const declaration: FakeAvailability = {
    id: fakePeopleId(ctx.state),
    personId: link.personId,
    personLinkId: link.id,
    ...input,
    declaredBy: ctx.me.user.id,
    declaredAt: now,
    createdAt: now,
    modifiedAt: null,
    version: 1,
  };
  ctx.state.availabilities.push(declaration);
  audit(ctx, {
    eventType: 'PPL.AVAILABILITY_SAVED',
    entityType: 'AvailabilityDeclaration',
    entityId: declaration.id,
    tenantId: link.tenantId,
  });
  return created(toAvailabilityResponse(declaration), etagOf(declaration.version));
}

async function putAvailability(
  request: Request,
  ctx: PeopleRouteContext,
  link: FakeLink,
  declarationId: string,
  requireChangeReason: boolean,
): Promise<Response> {
  const declaration = availabilityOf(ctx, link, declarationId);
  requireIfMatch(request, etagOf(declaration.version));
  const body = await readJsonBody(request);
  const reason = requireChangeReason
    ? requireReason(body)
    : { code: 'PPL_SELF_AVAILABILITY', text: null };
  const input = normalizeAvailability(link, body, declaration.kind);
  Object.assign(declaration, input);
  declaration.declaredBy = ctx.me.user.id;
  declaration.declaredAt = new Date().toISOString();
  declaration.version += 1;
  declaration.modifiedAt = declaration.declaredAt;
  audit(ctx, {
    eventType: 'PPL.AVAILABILITY_SAVED',
    entityType: 'AvailabilityDeclaration',
    entityId: declaration.id,
    tenantId: link.tenantId,
    reason,
  });
  return ok(toAvailabilityResponse(declaration), etagOf(declaration.version));
}

// ---- Ausências ----

function toAbsenceResponse(item: FakeAbsence) {
  const { removed: _removed, ...rest } = item;
  return rest;
}

function normalizeAbsence(body: unknown) {
  const errors: FieldErrorItem[] = [];
  const kind = str(body, 'kind');
  if (kind === null || !ABSENCE_KINDS.has(kind)) {
    errors.push(fieldError('kind', kind === null ? 'required' : 'invalid'));
  }
  const startDate = str(body, 'startDate');
  const endDate = str(body, 'endDate');
  checkDate(errors, 'startDate', startDate);
  checkDate(errors, 'endDate', endDate);
  if (isIsoDate(endDate) && isIsoDate(startDate) && endDate < startDate) {
    errors.push(fieldError('endDate', 'invalid'));
  }
  const startTime = str(body, 'startTime');
  const endTime = str(body, 'endTime');
  if ((startTime === null) !== (endTime === null)) {
    errors.push(fieldError(startTime === null ? 'startTime' : 'endTime', 'required'));
  } else if (startTime !== null && endTime !== null) {
    if (!TIME.test(startTime)) {
      errors.push(fieldError('startTime', 'invalid_time'));
    }
    if (!TIME.test(endTime)) {
      errors.push(fieldError('endTime', 'invalid_time'));
    }
    if (
      TIME.test(startTime) &&
      TIME.test(endTime) &&
      timeToMinutes(startTime) >= timeToMinutes(endTime)
    ) {
      errors.push(fieldError('endTime', 'start_not_before_end'));
    }
  }
  const note = str(body, 'note');
  if (note !== null && note.length > 1000) {
    errors.push(fieldError('note', 'too_long'));
  }
  failIf(errors);
  return {
    kind: kind ?? '',
    startDate: startDate ?? '',
    endDate: endDate ?? '',
    startTime,
    endTime,
    blocksScheduling: record(body)['blocksScheduling'] !== false,
    note,
  };
}

/** Prévia de impacto pelo seam do SCH: indisponível até o M11 (`TODO(GIY-132)`), lista vazia. */
const NO_SCHEDULING_IMPACT = { schedulingAvailable: false, appointments: [] };

/** `restrictToTenantId`: na rota do tenant só as ausências que ele registrou (origem — OPEN-098); rede e autosserviço veem todas. */
function listAbsences(
  request: Request,
  ctx: PeopleRouteContext,
  person: FakePerson,
  restrictToTenantId: string | null,
): Response {
  const url = new URL(request.url);
  const from = url.searchParams.get('from');
  const to = url.searchParams.get('to');
  return ok(
    ctx.state.absences
      .filter((item) => item.personId === person.id && !item.removed)
      .filter((item) => restrictToTenantId === null || item.tenantId === restrictToTenantId)
      .filter((item) => (!from || item.endDate >= from) && (!to || item.startDate <= to))
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .map(toAbsenceResponse),
  );
}

async function postAbsence(
  request: Request,
  ctx: PeopleRouteContext,
  person: FakePerson,
  tenantId: string | null,
): Promise<Response> {
  const input = normalizeAbsence(await readJsonBody(request));
  const absence: FakeAbsence = {
    id: fakePeopleId(ctx.state),
    personId: person.id,
    tenantId,
    ...input,
    createdAt: new Date().toISOString(),
    createdBy: ctx.me.user.id,
    removed: false,
  };
  ctx.state.absences.push(absence);
  audit(ctx, {
    eventType: 'PPL.ABSENCE_SAVED',
    entityType: 'Absence',
    entityId: absence.id,
    tenantId,
  });
  return created({ absence: toAbsenceResponse(absence), impact: NO_SCHEDULING_IMPACT });
}

async function previewAbsence(request: Request): Promise<Response> {
  normalizeAbsence(await readJsonBody(request));
  return ok(NO_SCHEDULING_IMPACT);
}

async function deleteAbsence(
  request: Request,
  ctx: PeopleRouteContext,
  person: FakePerson,
  absenceId: string,
  tenantId: string | null,
  selfScope: boolean,
): Promise<Response> {
  const absence = ctx.state.absences.find(
    (item) =>
      item.id === absenceId &&
      item.personId === person.id &&
      !item.removed &&
      (selfScope || tenantId === null || item.tenantId === tenantId), // o tenant só remove as suas (OPEN-098)
  );
  if (!absence) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const body = await readJsonBody(request);
  const reason =
    selfScope && !has(body, 'changeReason')
      ? { code: 'PPL_SELF_ABSENCE', text: null }
      : requireReason(body);
  absence.removed = true;
  audit(ctx, {
    eventType: 'PPL.ABSENCE_REMOVED',
    entityType: 'Absence',
    entityId: absence.id,
    tenantId,
    reason,
  });
  return ok(toAbsenceResponse(absence));
}

// ---- Onboarding e offboarding ----

function onboardingOf(ctx: PeopleRouteContext, link: FakeLink) {
  const found = ctx.state.onboarding.find((item) => item.personLinkId === link.id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function startOnboarding(ctx: PeopleRouteContext, link: FakeLink): Response {
  if (ctx.state.onboarding.some((item) => item.personLinkId === link.id)) {
    throw new FakeApiError(409, 'PPL_ONBOARDING_ALREADY_STARTED');
  }
  const onboarding = {
    id: fakePeopleId(ctx.state),
    personId: link.personId,
    personLinkId: link.id,
    items: buildOnboardingItems(link.functionCode),
    status: 'InProgress',
    startedAt: new Date().toISOString(),
    readyToOperateAt: null,
  };
  ctx.state.onboarding.push(onboarding);
  return created(onboarding);
}

async function markOnboardingItem(
  request: Request,
  ctx: PeopleRouteContext,
  link: FakeLink,
  itemCode: string,
): Promise<Response> {
  const onboarding = onboardingOf(ctx, link);
  const item = onboarding.items.find((candidate) => candidate.code === itemCode);
  if (!item) {
    throw validation([fieldError('itemCode', 'unknown')]);
  }
  const body = await readJsonBody(request);
  const evidence = str(body, 'evidenceDocumentId');
  if (evidence !== null && !UUID.test(evidence)) {
    throw validation([fieldError('evidenceDocumentId', 'invalid')]);
  }
  item.status = record(body)['notApplicable'] === true ? 'NotApplicable' : 'Done';
  item.completedAt = new Date().toISOString();
  item.completedBy = ctx.me.user.id;
  item.evidenceDocumentId = evidence;
  if (
    onboarding.items.every((candidate) => candidate.status !== 'Pending') &&
    onboarding.status !== 'ReadyToOperate'
  ) {
    onboarding.status = 'ReadyToOperate';
    onboarding.readyToOperateAt = new Date().toISOString();
    audit(ctx, {
      eventType: 'PPL.ONBOARDING_READY',
      entityType: 'OnboardingCase',
      entityId: onboarding.id,
      tenantId: link.tenantId,
    });
  }
  return ok(onboarding);
}

function offboardingOf(ctx: PeopleRouteContext, link: FakeLink) {
  const found = ctx.state.offboarding.find((item) => item.personLinkId === link.id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

type FakeOffboarding = ReturnType<typeof offboardingOf>;

/**
 * Cabeçalho que só o backend falso entende (nunca existe no servidor real): numa leitura do offboarding, `authz.access.revoked` faz o falso se comportar como o AUTHZ real —
 * a revogação de acesso é confirmada pelo sistema (item feito sem ator, `accessRevokedAt` gravado). Sem ele o item fica pendente, como logo depois de encerrar um vínculo.
 */
export const FAKE_SYSTEM_EVENT_HEADER = 'X-Fake-System-Event';

/** O aviso `authz.access.revoked` do AUTHZ (OPEN-117): o item de acesso feito pelo sistema; conclui o caso se era o último aberto. Idempotente. */
function confirmAccessRevokedBySystem(
  ctx: PeopleRouteContext,
  offboarding: FakeOffboarding,
  link: FakeLink,
): void {
  const item = offboarding.items.find(
    (candidate) => candidate.code === 'AccessRevocationConfirmed',
  );
  if (!item || item.status === 'Done') {
    return;
  }
  item.status = 'Done';
  item.completedAt = new Date().toISOString();
  item.completedBy = null;
  item.evidenceDocumentId = null;
  offboarding.accessRevokedAt = item.completedAt;
  completeOffboardingIfDone(ctx, offboarding, link);
}

function completeOffboardingIfDone(
  ctx: PeopleRouteContext,
  offboarding: FakeOffboarding,
  link: FakeLink,
): void {
  if (
    offboarding.items.every((candidate) => candidate.status === 'Done') &&
    offboarding.status !== 'Completed'
  ) {
    offboarding.status = 'Completed';
    audit(ctx, {
      eventType: 'PPL.OFFBOARDING_COMPLETED',
      entityType: 'OffboardingCase',
      entityId: offboarding.id,
      tenantId: link.tenantId,
    });
  }
}

async function markOffboardingItem(
  request: Request,
  ctx: PeopleRouteContext,
  link: FakeLink,
  itemCode: string,
): Promise<Response> {
  const offboarding = offboardingOf(ctx, link);
  const item = offboarding.items.find((candidate) => candidate.code === itemCode);
  if (!item) {
    throw validation([fieldError('itemCode', 'unknown')]);
  }
  if (itemCode === 'AccessRevocationConfirmed') {
    // OPEN-117: o item de acesso é só automático (authz.access.revoked); marcá-lo à mão é recusado.
    throw new FakeApiError(422, 'PPL_OFFBOARDING_ITEM_AUTOMATIC', {
      errors: [fieldError('itemCode', 'automatic')],
    });
  }
  const body = await readJsonBody(request);
  const evidence = str(body, 'evidenceDocumentId');
  if (evidence !== null && !UUID.test(evidence)) {
    throw validation([fieldError('evidenceDocumentId', 'invalid')]);
  }
  item.status = 'Done';
  item.completedAt = new Date().toISOString();
  item.completedBy = ctx.me.user.id;
  item.evidenceDocumentId = evidence;
  completeOffboardingIfDone(ctx, offboarding, link);
  return ok(offboarding);
}

// ---- Importação em lote (tenant) ----

interface ImportRowError {
  readonly field: string | null;
  readonly code: string;
}

/** Leitor CSV (RFC 4180: aspas, separador embutido, quebra de linha; `,` ou `;`; BOM); `null` = arquivo malformado. */
function parseCsv(raw: string): string[][] | null {
  const content = raw.startsWith('﻿') ? raw.slice(1) : raw;
  const firstLine = content.split(/\r?\n/, 1)[0] ?? '';
  const separator =
    (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const ch = content.charAt(index);
    if (quoted) {
      if (ch === '"') {
        if (content.charAt(index + 1) === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"' && cell === '') {
      quoted = true;
    } else if (ch === separator) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && content.charAt(index + 1) === '\n') {
        index += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (quoted) {
    return null;
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((item) => !(item.length === 1 && (item[0] ?? '').trim() === ''));
}

function hashOf(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(16);
}

function columnOfField(field: string, contactColumns: readonly string[]): string | null {
  const name = field.startsWith('link.') ? field.slice('link.'.length) : field;
  if (name.startsWith('contacts[')) {
    const close = name.indexOf(']');
    const position = Number(name.slice(9, close));
    return contactColumns[position] ?? 'contacts';
  }
  if (name === 'primarySiteId' || name === 'additionalSiteIds') {
    return 'primarySiteCode';
  }
  return name === '' ? null : name;
}

/** Erro de uma linha vira `{ coluna, código }` — nunca o valor (REQ-PPL-019). */
function rowErrorsOf(error: unknown, contactColumns: readonly string[]): ImportRowError[] {
  if (!(error instanceof FakeApiError)) {
    throw error;
  }
  const items = (error.extra?.['errors'] as FieldErrorItem[] | undefined) ?? [];
  if (items.length === 0) {
    return [{ field: null, code: error.errorCode }];
  }
  const generic = error.errorCode === 'PLT_VALIDATION';
  const mapped = items.map((item) => ({
    field: columnOfField(item.field, contactColumns),
    code: generic ? item.code : error.errorCode,
  }));
  return mapped.filter(
    (item, index) =>
      mapped.findIndex((other) => other.field === item.field && other.code === item.code) === index,
  );
}

interface PreparedRow {
  readonly number: number;
  readonly errors: ImportRowError[];
  readonly duplicate: boolean;
  readonly person: NormalizedPerson | null;
  readonly linkBody: Record<string, unknown> | null;
}

function prepareRows(
  ctx: PeopleRouteContext,
  tenant: FakePeopleTenant,
  table: readonly string[][],
  columns: ReadonlyMap<string, number>,
): PreparedRow[] {
  const header = table[0] ?? [];
  const seen = new Set<string>();
  return table.slice(1).map((cells, index): PreparedRow => {
    const number = index + 1;
    if (cells.length !== header.length) {
      return {
        number,
        errors: [{ field: null, code: 'columns_mismatch' }],
        duplicate: false,
        person: null,
        linkBody: null,
      };
    }
    const cell = (name: string): string | null => {
      const position = columns.get(name);
      const value = position === undefined ? '' : (cells[position] ?? '').trim();
      return value === '' ? null : value;
    };
    const errors: ImportRowError[] = [];
    const contactColumns: string[] = [];
    const contacts: Record<string, unknown>[] = [];
    if (cell('email') !== null) {
      contacts.push({ kind: 'Email', value: cell('email'), isPrimary: true });
      contactColumns.push('email');
    }
    if (cell('phone') !== null) {
      contacts.push({ kind: 'Phone', value: cell('phone'), isPrimary: true });
      contactColumns.push('phone');
    }
    const personBody = {
      givenName: cell('givenName'),
      familyName: cell('familyName'),
      socialName: cell('socialName'),
      country: cell('country'),
      federalCode: cell('federalCode'),
      birthDate: cell('birthDate'),
      preferredLocale: cell('preferredLocale'),
      contacts,
    };
    const dateColumns = ['birthDate', 'effectiveFrom', 'effectiveTo'];
    for (const name of dateColumns) {
      const value = cell(name);
      if (value !== null && !isIsoDate(value)) {
        errors.push({ field: name, code: 'invalid' });
        if (name === 'birthDate') {
          personBody.birthDate = '1000-01-01';
        }
      }
    }
    let person: NormalizedPerson | null = null;
    try {
      person = normalizePerson(ctx, personBody, true);
    } catch (error: unknown) {
      errors.push(
        ...rowErrorsOf(error, contactColumns).filter(
          (item) => !(item.field === 'birthDate' && errors.some((e) => e.field === 'birthDate')),
        ),
      );
    }
    const siteCode = cell('primarySiteCode');
    const siteCodes = Object.entries(tenant.siteCodes);
    let primarySiteId: string | null = null;
    if (siteCode === null) {
      if (siteCodes.length === 1) {
        primarySiteId = siteCodes[0]?.[1] ?? null;
      } else {
        errors.push({ field: 'primarySiteCode', code: 'required' });
      }
    } else {
      primarySiteId =
        siteCodes.find(([code]) => code.toLowerCase() === siteCode.toLowerCase())?.[1] ?? null;
      if (primarySiteId === null) {
        errors.push({ field: 'primarySiteCode', code: 'unknown' });
      }
    }
    const linkBody: Record<string, unknown> = {
      legalEntityId: tenant.legalEntityId,
      linkType: cell('linkType'),
      functionCode: cell('functionCode'),
      effectiveFrom: cell('effectiveFrom'),
      effectiveTo: cell('effectiveTo'),
      classificationJustification: cell('classificationJustification'),
      pjCnpj: cell('pjCnpj'),
      cooperativeMemberNumber: cell('cooperativeMemberNumber'),
      primarySiteId,
    };
    try {
      buildLink(ctx, { kind: 'tenant', tenant }, 'import-row', [], linkBody);
    } catch (error: unknown) {
      const found = rowErrorsOf(error, contactColumns).filter(
        (item) =>
          !errors.some((existing) => existing.field === item.field && existing.code === item.code),
      );
      errors.push(
        ...found.filter(
          (item) =>
            !(
              item.field === 'primarySiteCode' && errors.some((e) => e.field === 'primarySiteCode')
            ),
        ),
      );
    }
    let duplicate = false;
    if (person?.federalCode) {
      const key = `${person.country}:${person.federalCode.toUpperCase()}`;
      if (seen.has(key)) {
        errors.push({ field: 'federalCode', code: 'duplicate_in_file' });
      } else {
        seen.add(key);
        if (findDuplicate(ctx, person.country, person.federalCode)) {
          duplicate = true;
          errors.push({ field: null, code: 'PPL_DUPLICATE_PERSON' });
        }
      }
    }
    return {
      number,
      errors,
      duplicate,
      person: errors.length === 0 ? person : null,
      linkBody: errors.length === 0 ? linkBody : null,
    };
  });
}

function importHeader(table: readonly string[][]): Map<string, number> {
  const header = table[0] ?? [];
  const known = new Map(IMPORT_KNOWN_COLUMNS.map((name) => [name.toLowerCase(), name]));
  const columns = new Map<string, number>();
  const errors: FieldErrorItem[] = [];
  header.forEach((name, index) => {
    const canonical = known.get(name.trim().toLowerCase());
    if (!canonical) {
      errors.push(fieldError(`columns[${String(index + 1)}]`, 'unknown'));
    } else if (columns.has(canonical)) {
      errors.push(fieldError(`columns.${canonical}`, 'duplicate'));
    } else {
      columns.set(canonical, index);
    }
  });
  for (const name of IMPORT_REQUIRED_COLUMNS) {
    if (!columns.has(name)) {
      errors.push(fieldError(`columns.${name}`, 'required'));
    }
  }
  if (errors.length > 0) {
    throw new FakeApiError(422, 'PPL_IMPORT_INVALID_FILE', { errors: errors.slice(0, 50) });
  }
  return columns;
}

function toBatchResponse(batch: FakeImportBatch, withRows: boolean, replayed = false) {
  const { tenantId: _tenantId, fingerprint: _fingerprint, rowReport, ...rest } = batch;
  return { ...rest, rows: withRows ? rowReport : null, replayed };
}

async function importPeople(
  request: Request,
  ctx: PeopleRouteContext,
  tenant: FakePeopleTenant,
): Promise<Response> {
  need(ctx, { kind: 'tenant', tenant }, 'person.import');
  requireStepUp(ctx);
  const body = await readJsonBody(request);
  const fileName = str(body, 'fileName');
  const content =
    typeof record(body)['content'] === 'string' ? (record(body)['content'] as string) : null;
  const modeText = str(body, 'mode') ?? 'Atomic';
  const errors: FieldErrorItem[] = [];
  if (fileName === null) {
    errors.push(fieldError('fileName', 'required'));
  }
  if (content === null || content.trim() === '') {
    errors.push(fieldError('content', 'required'));
  }
  if (modeText !== 'Atomic' && modeText !== 'Partial') {
    errors.push(fieldError('mode', 'invalid'));
  }
  failIf(errors);
  const text = content ?? '';
  if (text.length > IMPORT_MAX_BYTES) {
    throw new FakeApiError(422, 'PPL_IMPORT_TOO_LARGE');
  }
  const table = parseCsv(text);
  if (!table || table.length === 0) {
    throw new FakeApiError(422, 'PPL_IMPORT_INVALID_FILE');
  }
  const columns = importHeader(table);
  if (table.length - 1 > ctx.state.settings.importMaxRows) {
    throw new FakeApiError(422, 'PPL_IMPORT_TOO_LARGE');
  }
  const mode = modeText === 'Partial' ? 'Partial' : 'Atomic';
  const fingerprint = hashOf(`${mode}:${text}`);
  const previous = ctx.state.imports.find(
    (item) =>
      item.tenantId === tenant.id &&
      item.fingerprint === fingerprint &&
      ['Applied', 'PartiallyApplied'].includes(item.status),
  );
  if (previous) {
    return ok(toBatchResponse(previous, true, true));
  }
  const rows = prepareRows(ctx, tenant, table, columns);
  const duplicateCount = rows.filter((row) => row.duplicate).length;
  const since = new Date(Date.now() - 86_400_000).toISOString();
  const recentDuplicates = ctx.state.imports
    .filter((item) => item.tenantId === tenant.id && item.startedAt >= since)
    .reduce((sum, item) => sum + Number(item.duplicateCount), 0);
  if (recentDuplicates + duplicateCount > ctx.state.settings.importMaxDuplicateRowsPerDay) {
    throw new FakeApiError(423, 'PPL_IMPORT_DUPLICATE_BUDGET_EXCEEDED');
  }
  const startedAt = new Date().toISOString();
  const aborted = duplicateCount > ctx.state.settings.importMaxDuplicateRows;
  const hasErrors = rows.some((row) => row.errors.length > 0);
  const apply = !aborted && (mode === 'Partial' || !hasErrors);
  const report: ImportRowResponse[] = [];
  let seenDuplicates = 0;
  let appliedCount = 0;
  for (const row of rows) {
    seenDuplicates += row.duplicate ? 1 : 0;
    if (aborted && seenDuplicates > ctx.state.settings.importMaxDuplicateRows) {
      report.push({
        row: row.number,
        outcome: 'NotProcessed',
        personId: null,
        linkId: null,
        errors: [],
      });
      continue;
    }
    if (row.errors.length > 0) {
      report.push({
        row: row.number,
        outcome: 'Rejected',
        personId: null,
        linkId: null,
        errors: row.errors.map((item) => ({ field: item.field, code: item.code })),
      });
      continue;
    }
    if (!apply || !row.person || !row.linkBody) {
      report.push({
        row: row.number,
        outcome: 'NotApplied',
        personId: null,
        linkId: null,
        errors: [],
      });
      continue;
    }
    const scope: Scope = { kind: 'tenant', tenant };
    const personId = fakePeopleId(ctx.state);
    const draft = buildLink(ctx, scope, personId, [], row.linkBody);
    const person: FakePerson = {
      id: personId,
      givenName: row.person.givenName,
      familyName: row.person.familyName,
      socialName: row.person.socialName,
      country: row.person.country,
      federalCode: row.person.federalCode,
      birthDate: row.person.birthDate,
      preferredLocale: row.person.preferredLocale,
      photoDocumentId: null,
      status: 'Active',
      contacts: row.person.contacts,
      createdAt: startedAt,
      modifiedAt: null,
      version: 1,
      visibility: {},
    };
    ctx.state.people.push(person);
    const link = commitLink(ctx, scope, person, draft);
    appliedCount += 1;
    report.push({ row: row.number, outcome: 'Applied', personId, linkId: link.id, errors: [] });
  }
  const errorCount = rows.filter((row) => row.errors.length > 0).length;
  const status =
    aborted || appliedCount === 0 ? 'Rejected' : errorCount > 0 ? 'PartiallyApplied' : 'Applied';
  const batch: FakeImportBatch = {
    id: fakePeopleId(ctx.state),
    tenantId: tenant.id,
    fingerprint,
    fileName: fileName ?? '',
    mode,
    status,
    rowCount: rows.length,
    appliedCount,
    errorCount,
    duplicateCount,
    abortReason: aborted ? 'duplicate_limit' : null,
    startedAt,
    completedAt: new Date().toISOString(),
    replayed: false,
    rows: null,
    rowReport: report,
  };
  ctx.state.imports.push(batch);
  audit(ctx, {
    eventType: status === 'Rejected' ? 'PPL.IMPORT_REJECTED' : 'PPL.IMPORT_APPLIED',
    entityType: 'PeopleImportBatch',
    entityId: batch.id,
    tenantId: tenant.id,
  });
  return created(toBatchResponse(batch, true));
}

function listImports(
  request: Request,
  ctx: PeopleRouteContext,
  tenant: FakePeopleTenant,
): Response {
  need(ctx, { kind: 'tenant', tenant }, 'person.import');
  const url = new URL(request.url);
  const page = Math.max(1, Number(url.searchParams.get('page') ?? '1') || 1);
  const pageSize =
    Number(url.searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE;
  if (pageSize > MAX_PAGE_SIZE) {
    throw new FakeApiError(422, 'PLT_PAGE_LIMIT_EXCEEDED');
  }
  const own = ctx.state.imports
    .filter((item) => item.tenantId === tenant.id)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return ok({
    items: own
      .slice((page - 1) * pageSize, page * pageSize)
      .map((item) => toBatchResponse(item, false)),
    page,
    pageSize,
    total: own.length,
  });
}

function getImport(ctx: PeopleRouteContext, tenant: FakePeopleTenant, batchId: string): Response {
  need(ctx, { kind: 'tenant', tenant }, 'person.import');
  const batch = ctx.state.imports.find(
    (item) => item.id === batchId && item.tenantId === tenant.id,
  );
  if (!batch) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return ok(toBatchResponse(batch, true));
}

// ---- Point-in-time (rede; propósito) ----

function pointInTime(request: Request, ctx: PeopleRouteContext): Response {
  const scope: Scope = { kind: 'network' };
  need(ctx, scope, 'people.history.read');
  const purpose = requirePurpose(request);
  const url = new URL(request.url);
  const date = url.searchParams.get('date');
  if (!isIsoDate(date) || date > todayIso()) {
    throw validation([fieldError('date', date === null ? 'required' : 'invalid')]);
  }
  const tenantId = url.searchParams.get('tenantId');
  const limit = Math.min(
    Number(url.searchParams.get('limit') ?? String(MAX_PAGE_SIZE)) || MAX_PAGE_SIZE,
    MAX_PAGE_SIZE,
  );
  const cursor = url.searchParams.get('cursor');
  let offset = 0;
  if (cursor) {
    offset = /^c\d+$/.test(cursor) ? Number(cursor.slice(1)) : -1;
    if (offset < 0) {
      throw new FakeApiError(422, 'PLT_INVALID_CURSOR');
    }
  }
  const people = ctx.state.people
    .map((person) => ({
      person,
      links: ctx.state.links.filter(
        (link) =>
          link.personId === person.id &&
          (!tenantId || link.tenantId === tenantId) &&
          link.effectiveFrom <= date &&
          (link.effectiveTo === null || link.effectiveTo >= date),
      ),
    }))
    .filter((entry) => entry.links.length > 0)
    .sort((a, b) => displayNameOf(a.person).localeCompare(displayNameOf(b.person), 'pt-BR'));
  const page = people.slice(offset, offset + limit);
  audit(ctx, {
    eventType: 'PPL.HISTORY_QUERIED',
    entityType: 'Person',
    entityId: ctx.me.user.id,
    purpose,
    permission: 'people.history.read',
    after: { date, tenantId, count: page.length },
  });
  return ok({
    asKnownAt: new Date().toISOString(),
    date,
    items: page.map(({ person, links }) => ({
      personId: person.id,
      displayName: displayNameOf(person),
      links: links.map((link) => ({
        linkId: link.id,
        legalEntityId: link.legalEntityId,
        tenantId: link.tenantId,
        linkType: link.linkType,
        functionCode: link.functionCode,
        classificationStatus: link.classificationStatus,
        effectiveFrom: link.effectiveFrom,
        effectiveTo: link.effectiveTo,
        recordedAt: link.createdAt,
      })),
      certifications: ctx.state.certifications
        .filter(
          (item) =>
            item.personId === person.id &&
            item.status !== 'Revoked' &&
            item.issuedAt <= date &&
            item.validUntil >= date,
        )
        .map((item) => ({
          certificationId: item.id,
          typeCode: item.typeCode,
          studyId: item.studyId,
          issuedAt: item.issuedAt,
          validUntil: item.validUntil,
          verifiedAt: item.verifiedAt,
          // Sem ciclos arquivados no fake: toda certificação vem da linha atual (o servidor também devolve `Archived`).
          source: 'Current',
          recordedAt: item.createdAt,
        })),
    })),
    nextCursor: offset + limit < people.length ? `c${String(offset + limit)}` : null,
  });
}

// ---- "Meu cadastro" (SelfScope: a pessoa vem da claim `pid`) ----

function ownPerson(ctx: PeopleRouteContext): FakePerson {
  const id = ctx.me.person?.id;
  const person = id ? findPerson(ctx, id) : undefined;
  if (!person) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return person;
}

function ownLink(ctx: PeopleRouteContext, person: FakePerson, linkId: string): FakeLink {
  const found = ctx.state.links.find((link) => link.id === linkId && link.personId === person.id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function meRoutes(request: Request, ctx: PeopleRouteContext): RouteDef[] {
  const self = (): FakePerson => ownPerson(ctx);
  return [
    {
      method: 'GET',
      pattern: ['person'],
      handler: () => {
        const person = self();
        return ok(toPersonDetail(person, []), etagOf(person.version));
      },
    },
    {
      method: 'GET',
      pattern: ['person', 'links'],
      handler: () => {
        const today = todayIso();
        return ok(
          ctx.state.links
            .filter((link) => link.personId === self().id)
            .map((link) => toLinkResponse(link, today)),
        );
      },
    },
    {
      method: 'GET',
      pattern: ['person', 'certifications'],
      handler: () =>
        ok(
          ctx.state.certifications
            .filter((item) => item.personId === self().id)
            .map((item) => toCertificationResponse(ctx, item)),
        ),
    },
    {
      method: 'PUT',
      pattern: ['person', 'contacts'],
      handler: async () => {
        const person = self();
        const body = await readJsonBody(request);
        if (!Array.isArray(record(body)['contacts'])) {
          throw validation([fieldError('contacts', 'required')]);
        }
        person.contacts = normalizeContacts(ctx, record(body)['contacts'], 'contacts');
        bump(person);
        audit(ctx, {
          eventType: 'PPL.PERSON_CONTACT_UPDATED',
          entityType: 'Person',
          entityId: person.id,
          reason: { code: 'PPL_SELF_CONTACTS', text: null },
        });
        return ok(toPersonDetail(person, []), etagOf(person.version));
      },
    },
    {
      method: 'GET',
      pattern: ['person', 'links', ':linkId', 'availability'],
      handler: (p) => {
        const link = ownLink(ctx, self(), param(p, 'linkId'));
        return ok(
          ctx.state.availabilities
            .filter((item) => item.personLinkId === link.id)
            .map(toAvailabilityResponse),
        );
      },
    },
    {
      method: 'POST',
      pattern: ['person', 'links', ':linkId', 'availability'],
      handler: (p) => postAvailability(request, ctx, ownLink(ctx, self(), param(p, 'linkId'))),
    },
    {
      method: 'GET',
      pattern: ['person', 'links', ':linkId', 'availability', ':declarationId'],
      handler: (p) => {
        const declaration = availabilityOf(
          ctx,
          ownLink(ctx, self(), param(p, 'linkId')),
          param(p, 'declarationId'),
        );
        return ok(toAvailabilityResponse(declaration), etagOf(declaration.version));
      },
    },
    {
      method: 'PUT',
      pattern: ['person', 'links', ':linkId', 'availability', ':declarationId'],
      handler: (p) =>
        putAvailability(
          request,
          ctx,
          ownLink(ctx, self(), param(p, 'linkId')),
          param(p, 'declarationId'),
          false,
        ),
    },
    {
      method: 'GET',
      pattern: ['person', 'absences'],
      handler: () => listAbsences(request, ctx, self(), null),
    },
    {
      method: 'POST',
      pattern: ['person', 'absences'],
      handler: () => postAbsence(request, ctx, self(), null),
    },
    {
      method: 'POST',
      pattern: ['person', 'absences', 'preview-impact'],
      handler: () => previewAbsence(request),
    },
    {
      method: 'DELETE',
      pattern: ['person', 'absences', ':absenceId'],
      handler: (p) => deleteAbsence(request, ctx, self(), param(p, 'absenceId'), null, true),
    },
  ];
}

// ---- Tabela de rotas por escopo ----

function scopedRoutes(request: Request, ctx: PeopleRouteContext, scope: Scope): RouteDef[] {
  const person = (p: Params): FakePerson => visiblePerson(ctx, scope, param(p, 'personId'));
  const link = (p: Params): FakeLink => visibleLink(ctx, scope, person(p), param(p, 'linkId'));
  const network = scope.kind === 'network';
  const routes: RouteDef[] = [
    { method: 'GET', pattern: [], handler: () => listPeople(request, ctx, scope) },
    { method: 'POST', pattern: [], handler: () => createPerson(request, ctx, scope) },
    { method: 'GET', pattern: ['functions'], handler: () => listFunctions(request, ctx, scope) },
    {
      method: 'GET',
      pattern: ['legal-entities'],
      handler: () => listLegalEntities(ctx, scope),
    },
    {
      method: 'GET',
      pattern: ['function-link-matrix'],
      handler: () => getCurrentMatrix(ctx, scope),
    },
    { method: 'GET', pattern: ['certification-types'], handler: () => ok(CERTIFICATION_TYPES) },
    { method: 'GET', pattern: ['professional-registration-boards'], handler: () => ok(BOARDS) },
    { method: 'GET', pattern: ['skill-catalog'], handler: () => ok(SKILLS) },
    {
      method: 'GET',
      pattern: [':personId'],
      handler: (p) => getPerson(ctx, scope, param(p, 'personId')),
    },
    {
      method: 'PUT',
      pattern: [':personId'],
      handler: (p) => updatePerson(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'sensitive'],
      handler: (p) => getSensitive(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'links'],
      handler: (p) => listLinks(ctx, scope, param(p, 'personId')),
    },
    {
      method: 'POST',
      pattern: [':personId', 'links'],
      handler: (p) => postLink(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId'],
      handler: (p) => getLink(ctx, scope, param(p, 'personId'), param(p, 'linkId')),
    },
    {
      method: 'PUT',
      pattern: [':personId', 'links', ':linkId'],
      handler: (p) => putLink(request, ctx, scope, param(p, 'personId'), param(p, 'linkId')),
    },
    {
      method: 'POST',
      pattern: [':personId', 'links', ':linkId', 'end'],
      handler: (p) => endLink(request, ctx, scope, param(p, 'personId'), param(p, 'linkId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'licenses'],
      handler: (p) => {
        need(ctx, scope, 'person.credential.read');
        return ok(
          ctx.state.licenses
            .filter((item) => item.personId === person(p).id)
            .map((item) => toLicenseResponse(ctx, item)),
        );
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'licenses'],
      handler: (p) => postLicense(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'licenses', ':licenseId'],
      handler: (p) => {
        need(ctx, scope, 'person.credential.read');
        const license = licenseOf(ctx, person(p), param(p, 'licenseId'));
        return ok(toLicenseResponse(ctx, license), etagOf(license.version));
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'licenses', ':licenseId'],
      handler: (p) => putLicense(request, ctx, scope, param(p, 'personId'), param(p, 'licenseId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'certifications'],
      handler: (p) => {
        need(ctx, scope, 'person.credential.read');
        return ok(
          ctx.state.certifications
            .filter((item) => item.personId === person(p).id)
            .map((item) => toCertificationResponse(ctx, item)),
        );
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'certifications'],
      handler: (p) => postCertification(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'certifications', ':certificationId'],
      handler: (p) => {
        need(ctx, scope, 'person.credential.read');
        const certification = certificationOf(ctx, person(p), param(p, 'certificationId'));
        return ok(toCertificationResponse(ctx, certification), etagOf(certification.version));
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'certifications', ':certificationId'],
      handler: (p) =>
        putCertification(request, ctx, scope, param(p, 'personId'), param(p, 'certificationId')),
    },
    {
      method: 'POST',
      pattern: [':personId', 'certifications', ':certificationId', 'verify'],
      handler: (p) =>
        verifyCertification(request, ctx, scope, param(p, 'personId'), param(p, 'certificationId')),
    },
    {
      method: 'POST',
      pattern: [':personId', 'certifications', ':certificationId', 'revoke'],
      handler: (p) =>
        revokeCertification(request, ctx, scope, param(p, 'personId'), param(p, 'certificationId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'skills'],
      handler: (p) => listSkills(ctx, scope, param(p, 'personId')),
    },
    {
      method: 'PUT',
      pattern: [':personId', 'skills'],
      handler: (p) => putSkills(request, ctx, scope, param(p, 'personId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'work-schedules'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return ok(
          ctx.state.schedules
            .filter((item) => item.personLinkId === link(p).id)
            .map(toScheduleResponse),
        );
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'links', ':linkId', 'work-schedules'],
      handler: (p) => postSchedule(request, ctx, scope, param(p, 'personId'), param(p, 'linkId')),
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'work-schedules', ':scheduleId'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        const schedule = scheduleOf(ctx, link(p), param(p, 'scheduleId'));
        return ok(toScheduleResponse(schedule), etagOf(schedule.version));
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'links', ':linkId', 'work-schedules', ':scheduleId'],
      handler: (p) =>
        putSchedule(
          request,
          ctx,
          scope,
          param(p, 'personId'),
          param(p, 'linkId'),
          param(p, 'scheduleId'),
        ),
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'availability'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return ok(
          ctx.state.availabilities
            .filter((item) => item.personLinkId === link(p).id)
            .map(toAvailabilityResponse),
        );
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'links', ':linkId', 'availability'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return postAvailability(request, ctx, link(p));
      },
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'availability', ':declarationId'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        const declaration = availabilityOf(ctx, link(p), param(p, 'declarationId'));
        return ok(toAvailabilityResponse(declaration), etagOf(declaration.version));
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'links', ':linkId', 'availability', ':declarationId'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return putAvailability(request, ctx, link(p), param(p, 'declarationId'), true);
      },
    },
    {
      method: 'GET',
      pattern: [':personId', 'absences'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return listAbsences(request, ctx, person(p), tenantIdOf(scope));
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'absences'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return postAbsence(request, ctx, person(p), tenantIdOf(scope));
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'absences', 'preview-impact'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        person(p);
        return previewAbsence(request);
      },
    },
    {
      method: 'DELETE',
      pattern: [':personId', 'absences', ':absenceId'],
      handler: (p) => {
        need(ctx, scope, 'person.schedule.manage');
        return deleteAbsence(
          request,
          ctx,
          person(p),
          param(p, 'absenceId'),
          tenantIdOf(scope),
          false,
        );
      },
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'onboarding'],
      handler: (p) => {
        need(ctx, scope, 'person.manage');
        return ok(onboardingOf(ctx, link(p)));
      },
    },
    {
      method: 'POST',
      pattern: [':personId', 'links', ':linkId', 'onboarding'],
      handler: (p) => {
        need(ctx, scope, 'person.manage');
        return startOnboarding(ctx, link(p));
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'links', ':linkId', 'onboarding', 'items', ':itemCode'],
      handler: (p) => {
        need(ctx, scope, 'person.manage');
        return markOnboardingItem(request, ctx, link(p), decodeURIComponent(param(p, 'itemCode')));
      },
    },
    {
      method: 'GET',
      pattern: [':personId', 'links', ':linkId', 'offboarding'],
      handler: (p) => {
        need(ctx, scope, 'person.manage');
        const owner = link(p);
        const offboarding = offboardingOf(ctx, owner);
        if (request.headers.get(FAKE_SYSTEM_EVENT_HEADER) === 'authz.access.revoked') {
          confirmAccessRevokedBySystem(ctx, offboarding, owner);
        }
        return ok(offboarding);
      },
    },
    {
      method: 'PUT',
      pattern: [':personId', 'links', ':linkId', 'offboarding', 'items', ':itemCode'],
      handler: (p) => {
        need(ctx, scope, 'person.manage');
        return markOffboardingItem(request, ctx, link(p), decodeURIComponent(param(p, 'itemCode')));
      },
    },
  ];
  if (network) {
    routes.unshift(
      {
        method: 'GET',
        pattern: ['function-link-matrix', 'versions'],
        handler: () => {
          need(ctx, scope, 'people.matrix.read');
          return ok(
            [...ctx.state.matrixVersions]
              .sort((a, b) => b.version - a.version)
              .map(toMatrixSummary),
          );
        },
      },
      {
        method: 'GET',
        pattern: ['function-link-matrix', 'versions', ':versionId'],
        handler: (p) => {
          need(ctx, scope, 'people.matrix.read');
          const version = matrixVersionById(ctx, param(p, 'versionId'));
          return ok(toMatrixResponse(version), etagOf(version.rowVersion));
        },
      },
      {
        method: 'PUT',
        pattern: ['function-link-matrix'],
        handler: () => proposeMatrix(request, ctx),
      },
      { method: 'GET', pattern: ['point-in-time'], handler: () => pointInTime(request, ctx) },
      {
        method: 'GET',
        pattern: [':personId', 'links', 'transition-exceptions'],
        handler: (p) => {
          need(ctx, scope, 'person.link.read');
          const owner = person(p);
          return ok(
            ctx.state.exceptions
              .filter((item) => item.personId === owner.id)
              .map(toExceptionResponse),
          );
        },
      },
      {
        method: 'POST',
        pattern: [':personId', 'links', 'transition-exceptions'],
        handler: (p) => requestException(request, ctx, param(p, 'personId')),
      },
      {
        method: 'GET',
        pattern: [':personId', 'links', 'transition-exceptions', ':exceptionId'],
        handler: (p) => {
          need(ctx, scope, 'person.link.read');
          const exception = exceptionOf(ctx, person(p).id, param(p, 'exceptionId'));
          return ok(toExceptionResponse(exception), etagOf(exception.version));
        },
      },
    );
  } else {
    routes.unshift(
      {
        method: 'GET',
        pattern: ['imports'],
        handler: () => listImports(request, ctx, scope.tenant),
      },
      {
        method: 'POST',
        pattern: ['imports'],
        handler: () => importPeople(request, ctx, scope.tenant),
      },
      {
        method: 'GET',
        pattern: ['imports', ':batchId'],
        handler: (p) => getImport(ctx, scope.tenant, param(p, 'batchId')),
      },
    );
  }
  return routes;
}

// ---- Ponto de entrada ----

/**
 * `GET /authz/two-key` (pendências de duas chaves, "Meu → Pendências"): a Autorização lista só as aprovações dela; as de
 * pessoas (Matriz e exceção de transição) vivem no estado de Pessoas e são acrescentadas aqui — sem isso o Jurídico nunca
 * vê a segunda chave que deve dar. Mesma regra da listagem real: `mine` = onde sou uma das chaves; senão, as que outra
 * pessoa abriu e que eu posso assinar. Qualquer outro caminho devolve a resposta recebida, intacta.
 */
export async function withPeopleApprovals(
  request: Request,
  segments: readonly string[],
  response: Response,
  ctx: PeopleRouteContext,
): Promise<Response> {
  if (
    request.method !== 'GET' ||
    segments.length !== 2 ||
    segments[0] !== 'authz' ||
    segments[1] !== 'two-key' ||
    !response.ok
  ) {
    return response;
  }
  const url = new URL(request.url);
  const mine = url.searchParams.get('mine') === 'true';
  const status = url.searchParams.get('status') ?? 'PendingSecondKey';
  const permissions = networkPermissions(ctx);
  const own = ctx.state.approvals
    .filter((item) => item.status === status)
    .filter((item) =>
      mine
        ? item.firstActorUserId === ctx.me.user.id || item.secondActorUserId === ctx.me.user.id
        : item.firstActorUserId !== ctx.me.user.id &&
          permissions.has(item.requiredSecondPermission),
    )
    .map(toApprovalResponse);
  if (own.length === 0) {
    return response;
  }
  const authz = (await response.json()) as unknown[];
  return jsonResponse([...authz, ...own]);
}

/** Trata as rotas de Pessoas; `null` para qualquer outro caminho (o roteador segue para os demais módulos). */
export async function handlePeopleRoute(
  request: Request,
  segments: readonly string[],
  ctx: PeopleRouteContext,
): Promise<Response | null> {
  try {
    if (
      segments[0] === 'authz' &&
      segments[1] === 'two-key' &&
      segments[3] === 'second-key' &&
      request.method === 'POST'
    ) {
      return secondKey(ctx, segments[2] ?? '');
    }
    if (segments[0] === 'me' && segments[1] === 'person') {
      return await dispatch(request, segments.slice(1), meRoutes(request, ctx));
    }
    if (segments[0] === 'network' && segments[1] === 'people') {
      if (!ctx.me.networkAccess.hasAccess && !isSuperAdminUser(ctx.me)) {
        // REQ-AUTHZ-026: única situação em que a API responde 403 (nunca revela o que existe).
        throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
      }
      return await dispatch(
        request,
        segments.slice(2),
        scopedRoutes(request, ctx, { kind: 'network' }),
      );
    }
    if (segments[0] === 'tenants' && segments[1] && segments[2] === 'people') {
      const tenantId = segments[1];
      const visible =
        isSuperAdminUser(ctx.me) ||
        ctx.me.networkAccess.hasAccess ||
        ctx.me.contexts.some((context) => context.tenantId === tenantId);
      const tenant = ctx.state.tenants.find((item) => item.id === tenantId);
      if (!visible || !tenant) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      return await dispatch(
        request,
        segments.slice(3),
        scopedRoutes(request, ctx, { kind: 'tenant', tenant }),
      );
    }
    return null;
  } catch (error: unknown) {
    if (error instanceof FakeApiError) {
      return problemResponse(error.status, error.errorCode, ctx.correlationId, error.extra);
    }
    throw error;
  }
}
